/**
 * AŞAMA 1 SÖZLEŞMESİ — yerel çekirdek ile arayüz arasındaki tek arayüz.
 *
 * KURAL: Bu dosya Aşama 1 boyunca değişmez. C şeridi (yerel çekirdek) bunu
 * UYGULAR, E şeridi (arayüz) bunu ÇAĞIRIR, ikisi paralel çalışır.
 *
 * Aşama 0 ölçümünün dayattığı kısıt: veritabanı bir DedicatedWorker içinde
 * yaşamak ZORUNDA (`createSyncAccessHandle` yalnızca orada tanımlı). Bu
 * yüzden her çağrı asenkron ve arayüz veritabanına asla doğrudan dokunmaz.
 */

import type {
  AssetBinding,
  CurrencyCode,
  Decimal,
  InstrumentId,
  IsoDate,
  IsoTimestamp,
  Transaction,
  TransactionType,
} from "./index.ts";

// ---------------------------------------------------------------------------
// Motor mesajı (çok dilli arayüz, 27.10.2026)
// ---------------------------------------------------------------------------

/**
 * MOTOR MESAJI — motorun söylediği her şey KOD + değerler olarak gelir;
 * cümleyi arayüz, seçilen dilin sözlüğünden kurar (kullanıcı kararı
 * 27.10.2026, çok dilli arayüz: "Formül, uyarı ve 'bu değer niçin yok'
 * metinleri motordan kod olarak gelir, çeviriyi arayüz yapar; sebebi yine
 * motor söyler").
 *
 * Sebebin KENDİSİ motorda kalır: hangi kodun seçildiği, hangi değerlerin
 * iliştirildiği motorun kararıdır; arayüz yalnızca o kodun cümlesini yazar.
 * "Yok" ile "ölçülemedi", "sorulamadı" ile "bulunamadı" ayrımı AYRI
 * kodlardır — çeviride birleşemez.
 *
 * Kod adı `bölüm.ad` biçiminde, ASCII (ör. `deger.fiyatYok`). Değerler
 * metin, sayı ya da iç içe bir başka mesajdır (ör. bir başarısızlığın
 * sebebi); dizi verilirse arayüz öğeleri virgülle birleştirir. Tarihler ISO
 * (`2026-10-01`), ondalıklar sözleşmenin `Decimal` metni olarak gelir.
 *
 * Kodların tam listesi motorun kaynağından okunur: `mm("kod", …)`
 * çağrıları. Arayüz testi (`motorMesajlari.test.ts`) her kodun her dilin
 * sözlüğünde bulunduğunu ve sözlükte kullanılmayan kod kalmadığını denetler.
 */

export type MesajDegeri = string | number | MotorMesaji | readonly MotorMesaji[];

export interface MotorMesaji {
  readonly kod: string;
  readonly p?: Readonly<Record<string, MesajDegeri>>;
}

/** Bir motor mesajı kurar. Kod sabit bir dizgi olmalı (tarama testi onu okur). */
export function mm(kod: string, p?: Readonly<Record<string, MesajDegeri>>): MotorMesaji {
  return p === undefined ? { kod } : { kod, p };
}

/** Bir değerin mesaj olup olmadığı (iç içe değerleri çözmek için). */
export function motorMesajiMi(v: unknown): v is MotorMesaji {
  return typeof v === "object" && v !== null && !Array.isArray(v) && typeof (v as { kod?: unknown }).kod === "string";
}

// ---------------------------------------------------------------------------
// Hesaplar
// ---------------------------------------------------------------------------

export type AccountKind = "araci_kurum" | "banka" | "cuzdan" | "borsa" | "fiziki";

/**
 * HESAP RENGİ (kullanıcı kararı 12.10.2026): bir TANIMA rengi, yargı değil.
 * Kapalı liste; yargı renkleri (kırmızı "zarar", yeşil "kazanç", sarı
 * "dikkat") BİLEREK yok — hesabın adı uyarı gibi okunmasın. Kayıtta adı
 * durur, tonu arayüz temaya göre seçer.
 */
export const HESAP_RENKLERI = [
  "mavi",
  "lacivert",
  "mor",
  "eflatun",
  "turkuaz",
  "petrol",
  "kahverengi",
  "gri",
] as const;
export type HesapRengi = (typeof HESAP_RENKLERI)[number];

export interface Account {
  id: string;
  name: string;
  kind: AccountKind;
  /** Aracı kurum / banka adı. Serbest metin. */
  institution?: string;
  /** Hesabın ana para birimi; hareketler farklı para biriminde olabilir. */
  currency: CurrencyCode;
  openedAt?: IsoDate;
  closedAt?: IsoDate;
  note?: string;
  /** Hesabın tanıma rengi (yoksa renksiz). */
  renk?: HesapRengi;
  /**
   * DEVRE DIŞI (kullanıcı kararı 25.10.2026): hesap ve hareketleri Hesaplar
   * sayfası dışında hiçbir yerde görünmez, hiçbir toplama girmez; veri
   * silinmez. Yalnızca görünüm: fiyat takibi, yedek ve eşitleme sürer.
   * Hesap listesi, hareket sorgusu ve pozisyonlar bunları varsayılan olarak
   * dışarıda bırakır (açıkça istenmedikçe).
   */
  devreDisi?: boolean;

  // senkron alanları — Aşama 1'de yerine konur, Aşama 4'te kullanılır
  lamport: number;
  deviceId: string;
  deleted: boolean;
  updatedAt: IsoTimestamp;
}

/**
 * HESAP SİLİNİRKEN HAREKETLER (kullanıcı kararı 12.10.2026): ya onlar da
 * silinir (VARSAYILAN) ya da başka bir (canlı) hesaba taşınır. Taşınan
 * hareket baştan beri o hesaptaymış gibi sayılır — hareketin hesabı
 * değişir, ayrı bir "taşındı" izi tutulmaz.
 */
export type HesapHareketSecimi =
  | { readonly tur: "sil" }
  | { readonly tur: "tasi"; readonly hedefHesapId: string };

/** Silinmiş (ya da hiç olmayan) bir hesaba bağlı, kendisi silinmemiş hareketler. */
export interface OksuzHareketGrubu {
  readonly accountId: string;
  /** Hesabın adı; hesap satırı hiç yoksa kimliği. */
  readonly ad: string;
  readonly renk?: HesapRengi;
  readonly hareketSayisi: number;
}

/** Yeni kayıt oluştururken senkron alanları çekirdek doldurur. */
export type NewAccount = Omit<Account, "lamport" | "deviceId" | "deleted" | "updatedAt">;
/**
 * Hesap güncellemesi: verilmeyen alan KALIR; isteğe bağlı alana `null`
 * verilirse SİLİNİR (formda boşaltılan kurum, not, renk).
 */
export type AccountPatch = Partial<Omit<NewAccount, "institution" | "openedAt" | "closedAt" | "note" | "renk">> & {
  readonly institution?: string | null;
  readonly openedAt?: IsoDate | null;
  readonly closedAt?: IsoDate | null;
  readonly note?: string | null;
  readonly renk?: HesapRengi | null;
};
export type NewTransaction = Omit<Transaction, "lamport" | "deviceId" | "deleted" | "updatedAt">;

// ---------------------------------------------------------------------------
// Elle değerleme — merkezi kaynağı olmayan varlıklar
// ---------------------------------------------------------------------------

/**
 * Fiziki altın, halka açık olmayan pay, beslemede olmayan tahvil. Aşama 2'de
 * `unresolved` durumuna düşen araçlar da buraya akar.
 */
export interface ManualValuation {
  id: string;
  /** Hangi varlık: `AssetBinding` "manual" dalıyla eşleşir. */
  label: string;
  date: IsoDate;
  value: Decimal;
  currency: CurrencyCode;
  note?: string;

  lamport: number;
  deviceId: string;
  deleted: boolean;
  updatedAt: IsoTimestamp;
}

export type NewManualValuation = Omit<
  ManualValuation,
  "lamport" | "deviceId" | "deleted" | "updatedAt"
>;

/**
 * Aşama 1'de fiyatlar elle girilir; Aşama 2'de merkez devralır ve bu tablo
 * yalnızca merkezde bulunmayan araçlar için kalır.
 */
export interface ManualPrice {
  instrumentRef: string;
  date: IsoDate;
  close: Decimal;
  currency: CurrencyCode;

  // Diğer tüm tablolarda olduğu gibi burada da senkron alanları ZORUNLU:
  // bu satırlar Aşama 4'te senkronlanacak ve silinebilmeli. Aşama 1'de iki
  // şerit de bu eksiği bağımsız olarak bildirdi.
  lamport: number;
  deviceId: string;
  deleted: boolean;
  updatedAt: IsoTimestamp;
}

export type NewManualPrice = Omit<
  ManualPrice,
  "lamport" | "deviceId" | "deleted" | "updatedAt"
>;

// ---------------------------------------------------------------------------
// Sorgular
// ---------------------------------------------------------------------------

export interface TransactionFilter {
  accountIds?: readonly string[];
  /** `AssetBinding` üzerinden: izlenen araç kimliği ya da elle etiket. */
  instrumentIds?: readonly InstrumentId[];
  manualLabels?: readonly string[];
  types?: readonly TransactionType[];
  from?: IsoDate;
  to?: IsoDate;
  /** Varsayılan false — tombstone kayıtlar gizlidir. */
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
  /**
   * Devre dışı hesapların hareketleri de (25.10.2026). Verilmezse ve
   * `accountIds` de verilmezse dışarıda kalırlar; `accountIds` açıkça bir
   * hesap adlandırıyorsa o hesap devre dışı da olsa hareketleri gelir.
   */
  devreDisiDahil?: boolean;
}

export interface TransactionPage {
  /** Sıra GARANTİLİDİR: `TRANSACTION_SORT` (tradeDate DESC, id DESC). */
  rows: readonly Transaction[];
  /** Filtreye uyan TÜM kayıt sayısı (sayfalamadan bağımsız). */
  total: number;
}

/**
 * Aşama 1'in tek "hesap" çıktısı: hareketlerden türetilen ham pozisyon.
 * Maliyet yöntemi, şirket olayları ve çok para birimli değerleme AŞAMA 3'e ait
 * — burada kasten yok. Aşama 1 yalnızca "elimde ne var" sorusunu yanıtlar.
 */
export interface RawPosition {
  asset: AssetBinding;
  /** Alım − satım + bedelsiz vb. Ham toplam; düzeltme uygulanmamıştır. */
  quantity: Decimal;
  currency: CurrencyCode;
  firstTradeDate: IsoDate;
  lastTradeDate: IsoDate;
  transactionCount: number;
}

// ---------------------------------------------------------------------------
// FIFO parti defteri — AŞAMA 3, G ŞERİDİ
// ---------------------------------------------------------------------------

/**
 * Motorun kendisi `@parafon/local`in tarayıcı katmanında (`browser/fifo.ts`);
 * ÇIKTI ŞEKİLLERİ burada, çünkü rapor DTO'su onları olduğu gibi taşıyor ve
 * arayüz paketi tip için tarayıcı içi modüllere bağımlı olamaz.
 *
 * Kararların gerekçesi motorun dosya başlığında: aynı gün sırası, masraf ve
 * verginin ayrılığı, bölünmenin parti açmaması, maliyetin uydurulmaması.
 */

/** Sonuçtaki damgalar — hepsi "sayıyı gizlemeden eksikliği söylemek" için. */
export type FifoNoteKind =
  /** Elde olmayan miktar satıldı: defter eksik başlıyor olabilir. */
  | "oversold"
  /** Devir alınan miktarın birim maliyeti girilmemiş: maliyet 0 sayılmadı. */
  | "transfer_cost_unknown"
  /** Açık parti yokken bölünme/bedelsiz geldi: dağıtılacak parti yok. */
  | "rescale_without_lots"
  /** Aynı araçta birden fazla para birimi: toplam maliyet TEK sayıya inmez. */
  | "mixed_currency";

export interface FifoNote {
  readonly kind: FifoNoteKind;
  /** İlgili hareketin kimliği (varsa) — kullanıcı satırı bulabilsin. */
  readonly transactionId?: string;
  readonly date?: IsoDate;
  /** Ne olduğu: motor mesajı (cümleyi arayüz kurar). */
  readonly detail: MotorMesaji;
}

/** Açık parti: kalan miktar ve o miktara düşen maliyet. */
export interface FifoLot {
  /** Partiyi açan hareketin kimliği. Bölünme sonrası DEĞİŞMEZ. */
  readonly id: string;
  /** Edinme tarihi — FIFO sırasının anahtarı. */
  readonly date: IsoDate;
  readonly quantity: Decimal;
  readonly cost: Decimal;
  readonly currency: CurrencyCode;
  /** Maliyeti bilinmeyen devir: `cost` 0 ama bu bir iddia DEĞİL. */
  readonly costUnknown: boolean;
  /** Partiyi açan alış temettüyle yapıldı (`Transaction.temettuden`). */
  readonly temettuden?: boolean;
}

/** "Hangi partiden ne kadar düştü" — denetlenebilirliğin taşıyıcısı. */
export interface FifoConsumption {
  readonly lotId: string;
  readonly lotDate: IsoDate;
  readonly quantity: Decimal;
  readonly cost: Decimal;
  readonly costUnknown: boolean;
  /** Tüketilen parti temettüyle alınmıştı. */
  readonly temettuden?: boolean;
}

export interface FifoSale {
  readonly id: string;
  readonly date: IsoDate;
  readonly type: TransactionType;
  readonly quantity: Decimal;
  /** miktar × fiyat − masraf. */
  readonly proceeds: Decimal;
  /** Σ `from[].cost`. */
  readonly cost: Decimal;
  /** hasılat − maliyet. */
  readonly gain: Decimal;
  /** Kesilen vergi — kazanca DAHİL DEĞİL, ayrıca bildiriliyor. */
  readonly withheldTax: Decimal;
  readonly from: readonly FifoConsumption[];
  /** Karşılığı bulunamayan miktar. "0" değilse sonuç damgalı. */
  readonly shortQuantity: Decimal;
  /** Tüketilen partilerden en az biri maliyetsiz devirse kazanç eksik. */
  readonly costUnknown: boolean;
  readonly currency: CurrencyCode;
}

export interface FifoResult {
  readonly openLots: readonly FifoLot[];
  readonly quantity: Decimal;
  /**
   * Açık partilerin toplam maliyeti. `null` = tek sayıya indirilemiyor
   * (birden fazla para birimi) — uydurma bir toplam yerine yokluk.
   * Değerleme (I şeridi) bunu parti parti çevirerek çözüyor.
   */
  readonly cost: Decimal | null;
  readonly costCurrency: CurrencyCode | null;
  readonly sales: readonly FifoSale[];
  readonly realizedGain: Decimal | null;
  readonly withheldTax: Decimal | null;
  readonly notes: readonly FifoNote[];
}

// ---------------------------------------------------------------------------
// DEĞERLEME — AŞAMA 3, I ŞERİDİ
//
// AŞAMA 3'ÜN YASAĞI BU BÖLÜMÜN VAR OLMA SEBEBİ:
//
//   > Bir raporda hiçbir sayı, kullandığı fiyatın tarihini söylemeden
//   > gösterilemez.
//
// Yasak, "son fiyat kuralı"nın (LOCF) zorunlu tamamlayıcısı. Kullanıcı kararı
// şuydu: *"varlık fiyatını, sorgulanan tarihten önceki son fiyat olarak
// belirlemek yeterli olacaktır."* Karar doğru ve tatil takvimini gereksiz
// kıldı — ama TEK BAŞINA uygulanırsa sessiz bir yalana dönüşür: rapor
// "09.09.2026 değeri" der, kullanılan fiyat 25.08.2026'dandır ve kullanıcı
// bunu bilmez.
//
// O yüzden burada sayı ile tarihi ayrı alanlarda taşımak YETMİYOR; ikisi AYNI
// NESNEDE (`StampedAmount`). Bir tutarı dayanağı olmadan taşımak tip
// düzeyinde imkânsız: `amount` alanı yalnızca `basis` ile birlikte var.
// ---------------------------------------------------------------------------

/** Bir sayının dayandığı ölçümün TÜRÜ. */
export type ValuationBasisKind =
  /** Önbellekteki kapanış fiyatı (merkez verisi). */
  | "fiyat"
  /** Kullanıcının elle girdiği BİRİM fiyat (`manualPrices`). */
  | "elle-fiyat"
  /** Kullanıcının elle girdiği TOPLAM değer (`manualValuations`). */
  | "elle-deger"
  /** Döviz kuru. `source` hangi kaynak olduğunu söylemek ZORUNDA. */
  | "kur"
  /** Hareketin KENDİ fiyatı — FIFO maliyeti buna dayanıyor. */
  | "hareket";

/**
 * TEK bir ölçüm: ne, hangi kaynaktan, hangi tarihli.
 *
 * `forAskedDate` ayrımı bu yapının en ince ama en gerekli parçası. Bir
 * pozisyonun bugünkü değeri SORULAN güne bakan ölçümlere dayanır ve orada
 * bayatlık bir risktir. Buna karşılık 2019'da alınmış bir partinin maliyetini
 * rapor para birimine çevirmek 2019'un kurunu gerektirir ve o kur BAYAT
 * DEĞİLDİR — tarihsel olarak DOĞRU olan kurdur. İkisini aynı torbaya koymak
 * raporun her yerine sahte bir "7 yıl bayat" damgası basardı; ayırmamak ise
 * gerçek bayatlığı görünmez kılardı.
 */
export interface ValuationBasis {
  readonly kind: ValuationBasisKind;
  /** Neye ait: araç kimliği, "USD/TRY", elle etiket ya da hareket kimliği. */
  readonly ref: string;
  /**
   * Kaynak adı. Kur için ZORUNLU ("tcmb", "frankfurter", "birim") — vergi
   * hesabında kur kaynağı sorulabilir bir şey. Fiyatta null olabilir.
   */
  readonly source: string | null;
  /** Ölçümün GERÇEK tarihi (son fiyat kuralıyla bulunan gün). */
  readonly asOf: IsoDate;
  /** Ölçümün sorulduğu günden kaç gün geride kaldığı. */
  readonly staleDays: number;
  /** Ölçüm SORULAN güne mi bakıyor, GEÇMİŞ bir güne mi (bkz. tip notu). */
  readonly forAskedDate: boolean;
  /**
   * P-c · KAYNAĞIN TAKVİMİNE GÖRE BU ÖLÇÜMÜN EN GEÇ HANGİ GÜNE AİT OLMASI
   * BEKLENİRDİ (`expectedLatestDay`, sözleşme `central-api.ts`).
   *
   * `null` = takvim yok: kaynak gün içi fiyat veriyor (Yahoo, Binance — gün
   * içi takvim P-d'nin işi), ölçüm elle girilmiş, aracın künyesi henüz
   * gelmemiş ya da ölçüm geçmişe bakıyor. O durumda eski kural geçerli:
   * sorulan günden eski olan her ölçüm bayat.
   */
  readonly expectedAsOf: IsoDate | null;
  /**
   * P-d · GÜN İÇİ FİYATIN KENDİ ANI (Yahoo: son işlemin saati, UTC). Yalnızca
   * ölçüm bir anlık fiyatsa dolu; günlük bar ve kur bir GÜNE aittir, saati
   * yoktur ve uydurulmaz (alan hiç yok).
   */
  readonly asOfTs?: IsoTimestamp;
  /**
   * SEANS DIŞI FİYAT (04.11.2026): değer seans öncesi/sonrası fiyatla
   * hesaplandıysa hangisi (Ayarlar'daki seçim). Bilgidir, anormallik değil.
   */
  readonly seans?: "pre" | "post" | "night";
  /**
   * P-d · Merkezin bu fiyatı kaynaktan EN SON okuduğu an. Gün içi bayatlık
   * hükmü buna dayanır, `asOfTs`ye değil: seyrek işlem gören hissede son
   * işlem 40 dakika önce olabilir, seans kapanınca saat kapanışta donar —
   * ikisi de anormallik değil.
   */
  readonly observedAt?: IsoTimestamp;
  /**
   * P-d · Piyasa takvimine göre merkezin bu fiyatı EN GEÇ ne zaman
   * gözlemiş olması beklenirdi (`expectedObservedAfter`). Gün içi hüküm
   * verildiyse dolu; o zaman `expectedAsOf` yerine bu söylenir.
   */
  readonly expectedObservedAt?: IsoTimestamp;
  /**
   * BAYAT MI — MOTORUN HÜKMÜ. Arayüz bunu YENİDEN HESAPLAMAZ (§4h: bir
   * işaretin sebebini onu üreten motor söyler).
   *
   * Eskiden arayüz `staleDays > 0` diye bakıyordu: cumartesi günü cuma
   * fiyatı sarı görünüyordu, oysa TEFAS cumartesi yayımlamaz ve o fiyat
   * alınabilecek en taze fiyattır (yanlış alarm, 07 §4e ihlali). Yeni kural:
   * `expectedAsOf` varsa `asOf < expectedAsOf`; yoksa `staleDays > 0`.
   * Geçmişe bakan ölçüm (`forAskedDate: false`) HİÇBİR ZAMAN bayat değildir.
   */
  readonly stale: boolean;
}

/**
 * DAMGALI TUTAR — yasağın taşıyıcısı.
 *
 * `basis` HİÇ BOŞ OLMAZ: dayanağı olmayan bir sayı bu tipe hiç giremez.
 *
 * `asOf`/`staleDays` YALNIZCA `forAskedDate` ölçümlerinden hesaplanır:
 *   - `asOf`      = o ölçümlerin EN ESKİSİ,
 *   - `staleDays` = o ölçümlerin EN BÜYÜĞÜ.
 *
 * NİÇİN EN ESKİSİ: bir toplam, en bayat bileşeni kadar tazedir. Toplamı en
 * TAZE bileşeninin tarihiyle damgalamak, raporu olduğundan güncel gösterir —
 * yasağın engellemek istediği şeyin tam olarak toplam hâlidir.
 *
 * `asOf === null` demek: bu sayı bir güne DEĞERLENMİYOR, tamamen geçmiş
 * ölçümlere dayanıyor (çevrilmiş maliyet, gerçekleşmiş kazanç). O durumda
 * bayatlık kavramı geçersizdir ve `staleDays` de null olur — ama `basis`
 * yine doludur, yani sayı hâlâ dayanağını sayar.
 */
export interface StampedAmount {
  readonly amount: Decimal;
  readonly currency: CurrencyCode;
  readonly asOf: IsoDate | null;
  readonly staleDays: number | null;
  /**
   * Sorulan güne bakan dayanaklardan BİRİ bile bayatsa `true` — toplam, en
   * bayat bileşeni kadar tazedir. `asOf === null` ise daima `false`.
   */
  readonly stale: boolean;
  /** BOŞ OLAMAZ. */
  readonly basis: readonly ValuationBasis[];
}

/**
 * KAZANÇ ORANI — kazanç ÷ maliyet.
 *
 * `ratio` ondalık KESİR, yüzde değil: "0.123400" = %12,34. Yüzdeye çevirmek
 * gösterim katmanının işi; sayıyı yüzde olarak saklamak, iki yerde 100'le
 * çarpıp bir yerde unutmanın yolunu açar.
 *
 * `base` PAYDAYI taşıyor ve taşımak zorunda: bu raporun her sayısı
 * denetlenebilir olmalı ve "%12 kazanç" cümlesi tek başına denetlenemez —
 * neyin %12'si sorusunun cevabı burada. Payda her zaman rapor para
 * biriminde, çünkü pay da öyle.
 *
 * Bu yapı `null` olduğunda oran YOK demektir, SIFIR değil: maliyeti
 * bilinmeyen ya da sıfır olan bir pozisyonun oranı hesaplanamaz.
 */
export interface GainRatio {
  /**
   * Ondalık kesir, en çok 6 hane. Negatif olabilir (zarar). Sondaki sıfırlar
   * projenin diğer `Decimal` alanlarındaki gibi kırpılır: "0.2" = %20.
   */
  readonly ratio: Decimal;
  /** Oranın paydası — hangi maliyete bölündüğü. */
  readonly base: Decimal;
  readonly currency: CurrencyCode;
}

/** Değerleme damgaları — FIFO damgaları da bu listeye akıyor. */
export type ValuationNoteCode =
  | FifoNoteKind
  /** Araç için sorulan güne kadar HİÇ fiyat yok — pozisyon değerlenemedi. */
  | "fiyat_yok"
  /** Kur bulunamadı: değer aracın para biriminde var, raporda yok. */
  | "kur_yok"
  /** Elle değerlenen varlık için ne birim fiyat ne toplam değer girilmiş. */
  | "elle_deger_yok"
  /** Fiyatın para birimi, hareketlerin para biriminden FARKLI. */
  | "para_birimi_celiskisi"
  /**
   * İZLENEN ARAÇTA ELLE GİRİLEN FİYAT YOK SAYILDI.
   *
   * Önceki kod `elle_fiyat_tercih_edildi` idi ve tersini yapıyordu: elle
   * girilen fiyat daha taze ise kaynağınkini EZİYORDU. İzlenen bir araçta
   * (TEFAS fonu gibi) günlük fiyat tektir ve kaynaktan gelir; kullanıcının
   * yazdığı sayının onun yerine geçmesi, raporu kaynağın söylediğinden
   * koparır. Kayıt silinmiyor, yok sayıldığı söyleniyor.
   */
  | "elle_fiyat_yoksayildi"
  /** Pozisyon kapanmış (miktar 0): değerlenecek bir şey yok, kazanç var. */
  | "kapali_pozisyon"
  /**
   * NAKİT SATIRININ TUTARI OKUNAMADI. Kodlama kuralı (`CASH_TYPES` başlığı)
   * `quantity = "1", price = tutar` diyor; bu satırda `quantity × price`
   * sıfır çıkıyor ama `price` sıfır DEĞİL — yani `quantity` yazılmamış.
   *
   * Tutarı `price`tan okuyup "herhâlde budur" demek, sessizce doğru olma
   * İHTİMALİ olan bir sayı üretmek olurdu. Bu satır nakit akışına
   * KATILMIYOR ve pozisyonun nakit akışı `null` dönüyor.
   */
  | "nakit_tutar_okunamadi"
  /**
   * SERMAYE HAREKETİ GETİRİ DEĞİLDİR. `DEPOSIT`/`WITHDRAWAL` hesaba para
   * koymak ve çekmektir; getiriye katılırsa para yatırmak "kâr" gibi
   * görünür. Nakit akışına GİRMİYORLAR ve bu damgayla söyleniyor.
   *
   * Nakit BAKİYESİ ayrı bir sorudur ve ayrı bir yerde yanıtlanıyor:
   * `PortfolioReport.cashMovement`. Bu damga GETİRİ hesabına dair;
   * sermaye hareketi net nakit hareketine TAM olarak girer.
   */
  | "sermaye_hareketi_haric"
  /** Maliyetin bir kısmı bilinmiyor: kazanç EKSİK, sıfır sayılmadı. */
  | "maliyet_eksik"
  /**
   * DEFTERDEKİ PARA BİRİMİ KODU TANINMIYOR.
   *
   * `CURRENCIES` kapalı listesinde olmayan bir kod (örneğin "TL") kuru
   * hiçbir kaynaktan bulunamaz: merkez TCMB'nin yayımladığı kodları taşır,
   * "TL" onlardan biri değildir. Sonuç, kuru bulunamamış herhangi bir
   * pozisyondan AYIRT EDİLEMEYEN bir `kur_yok`tu — kullanıcı "kur yok"
   * görüyor, sebebinin kendi yazdığı kod olduğunu göremiyordu.
   *
   * Yeni kayıtta bu kodun girilmesi artık mümkün değil (giriş kapalı
   * listeden seçiliyor). Bu damga ESKİ defterler için: kayıt reddedilmiyor,
   * okunuyor ve sebebi adıyla söyleniyor.
   */
  | "para_birimi_taninmiyor"
  /**
   * NET NAKİT HAREKETİ OKUNAMADI — bu (hesap, para birimi) kovasında tutarı
   * okunamayan en az bir satır var (`nakit_tutar_okunamadi` ile aynı kök).
   *
   * Kovanın neti `null` dönüyor. Okunamayan satırı toplamdan sessizce
   * ÇIKARIP kalanı "net" diye yayımlamak, eksik olduğu söylenmeyen bir
   * toplam üretirdi; tür kırılımı yine de veriliyor ki kullanıcı bozuk
   * satırı bulabilsin.
   */
  | "nakit_hareketi_okunamadi"
  /**
   * NET NAKİT HAREKETİ RAPOR PARA BİRİMİNE ÇEVRİLEMEDİ.
   *
   * Kovanın kendi para birimindeki neti DOĞRU ve yayımlanıyor; çevrilemeyen
   * yalnızca rapor karşılığı. Bu yüzden ayrı bir damga: `kur_yok` ile
   * karıştırılırsa kullanıcı "nakit hesaplanamadı" sanır, oysa hesaplandı.
   */
  | "nakit_hareketi_cevrilemedi"
  /**
   * H2 · KAYNAKTA TEMETTÜ VAR, DEFTERDE YOK.
   *
   * Merkez (Yahoo) bu araç için bir temettü hak kullanım günü biliyor;
   * pozisyon o gün tutuluyordu ve defterde o tarihin çevresinde (−10/+60 gün)
   * `DIVIDEND` satırı yok. Hak kullanım gününden 21 gün geçmeden yanmaz
   * (kullanıcı kararı, 01.10.2026: ödeme günü kaynakta yok, ABD'de 2–4
   * hafta sonra gelebiliyor). Nakit akışı ve getiri bu temettü kadar EKSİK.
   */
  | "kaydedilmemis_temettu"
  /**
   * H2 · KAYNAKTA BÖLÜNME VAR, DEFTERDE YOK.
   *
   * Hak kullanım günü geçer geçmez yanar: kayıt yoksa ADET yanlış, bölünme
   * sonrası fiyatla çarpılınca piyasa değeri oran kadar yanlış çıkar.
   */
  | "kaydedilmemis_bolunme"
  /**
   * M1-B · BEDELSİZ VE RÜÇHAN BU ARAÇ İÇİN HİÇBİR KAYNAKTA YOK.
   *
   * BIST hissesinde (`equity_tr`) bedelsiz/rüçhan hiçbir dönem için
   * bilinmiyor (`CORPORATE_ACTION_GAPS`: KAP'ın API'si yok, Yahoo yalnızca
   * temettü ve bölünme veriyor). "Olay yok" DEĞİL, "olay bilinmiyor": adet ve
   * maliyet yalnızca defterdeki BONUS/RIGHTS satırlarına dayanır.
   *
   * BİLGİ NOTUDUR, ANORMALLİK DEĞİL (kullanıcı kararı 05.10.2026, seçenek a):
   * tonu `info`; tablo satırında rozet olmaz, rapor damgalarına çıkmaz,
   * yalnızca pozisyonun ayrıntısında açıklama olarak durur. Eskiden bunu
   * merkezin `corporate_action_gap` işareti taşıyordu (emekli, `e9c0cb0`).
   */
  | "bedelsiz_ruchan_bilinmiyor";

export interface ValuationNote {
  readonly code: ValuationNoteCode;
  /** İlgili varlık/araç/hareket — kullanıcı satırı bulabilsin. */
  readonly ref?: string;
  readonly date?: IsoDate;
  readonly detail: MotorMesaji;
}

/**
 * BİR TUTARIN NİÇİN OLMADIĞI — J'nin 1. kuralının ("hiçbir `null` sebebi
 * söylenmeden gösterilmez") taşıyıcısı.
 *
 * ----------------------------------------------------------------------------
 * NİÇİN SEBEBİ MOTOR SÖYLÜYOR, ARAYÜZ DEĞİL
 * ----------------------------------------------------------------------------
 *
 * Bir `null`ın sebebini bilen tek yer onu üreten koddur. Arayüz sebebi
 * raporun geri kalanından TÜRETMEYE kalkarsa ("satış listesi boşsa satış
 * yoktur, değilse kur yoktur") motorun kararını ikinci bir yerde yeniden
 * yazmış olur — `ports.ts`in tip aynasıyla aynı kusur, bu kez mantıkta: motor
 * değişir, ayna değişmez, ekran yanlış sebep söyler ve hiçbir test düşmez.
 *
 * ----------------------------------------------------------------------------
 * "YOK" İLE "ÖLÇÜLEMEDİ" AYNI ŞEY DEĞİL
 * ----------------------------------------------------------------------------
 *
 * - **`yok`**: ölçülecek bir şey YOK. Satışı olmayan varlığın gerçekleşmiş
 *   kazancı, temettüsü olmayan varlığın nakit akışı, kapalı pozisyonun
 *   değeri. Bu bir anormallik DEĞİL — defterin normal hâli.
 * - **`olculemedi`**: ölçülmesi GEREKİYORDU, olmadı. Fiyat yok, kur yok,
 *   okunamayan satır. Bakılacak bir şey VAR.
 *
 * Ayrım ekranın kuralını uygulayabilmek için şart: **ekranda görünen her
 * işaret bir anormallik demektir.** İkisini aynı işaretle göstermek, üç
 * TEFAS fonundan ibaret bir defterde (satış yok, temettü yok) her satıra üç
 * uyarı basıyordu — ve gerçek bir "fiyat yok" o kalabalıkta kayboluyordu.
 */
export type AbsenceKind = "yok" | "olculemedi";

export interface Absence {
  readonly kind: AbsenceKind;
  /** Sebep: motor mesajı; arayüz cümlesini kurar. Neyin eksik olduğunu ADIYLA söyler. */
  readonly reason: MotorMesaji;
}

/**
 * Pozisyon satırında sebebi söylenmek ZORUNDA olan tutarlar — tablonun
 * sütunları. `cashFlowNet` iç içe alanın (`cashFlow?.net`) düz adı.
 *
 * DEĞİŞMEZ (testte kilitli): listedeki bir alan `null` ise `absent`ta o
 * alanın kaydı VAR; doluysa YOK.
 *
 * `unitPrice` 11.10.2026'da eklendi: birim fiyat tablonun sütunu oldu.
 */
export const POSITION_AMOUNT_FIELDS = [
  "unitPrice",
  "reportValue",
  "costConverted",
  "unrealizedGain",
  "realizedGain",
  "cashFlowNet",
  "realizedReturn",
  "pay",
] as const;
export type PositionAmountField = (typeof POSITION_AMOUNT_FIELDS)[number];

/**
 * Rapor düzeyinde sebebi söylenmek zorunda olan toplamlar — özet kartları.
 * `totalCashFlowNet` = `totalCashFlow?.net`, `cashMovementTotal` =
 * `cashMovement?.total`. Değişmez `POSITION_AMOUNT_FIELDS`inkiyle aynı.
 */
export const REPORT_TOTAL_FIELDS = [
  "totalValue",
  "totalCost",
  "totalUnrealizedGain",
  "totalRealizedGain",
  "totalCashFlowNet",
  "totalRealizedReturn",
  "totalWithheldTax",
  "cashMovementTotal",
] as const;
export type ReportTotalField = (typeof REPORT_TOTAL_FIELDS)[number];

/**
 * TEK pozisyonun değerlemesi.
 *
 * `null` alanlar UYDURULMAMIŞ sayılardır: fiyat yoksa değer yok, kur yoksa
 * rapor para biriminde değer yok. Hiçbir `null`, sıfır anlamına GELMEZ.
 */
/**
 * NAKİT AKIŞININ TEK SATIRI — denetlenebilirliğin taşıyıcısı.
 *
 * `openLots` ve `sales` niçin raporda taşınıyorsa bu da o yüzden taşınıyor:
 * kullanıcı "bu temettü toplamı nereden çıktı" diye sorabilmeli ve cevabı
 * tek tek satırlar olmalı. Toplamı gösterip dökümü göstermemek, sayıyı
 * denetlenemez kılar.
 */
export interface CashFlowRow {
  readonly id: string;
  readonly date: IsoDate;
  readonly type: TransactionType;
  /** `quantity × price` — KENDİ para biriminde, İŞARETLİ: gelir +, gider −. */
  readonly amount: Decimal;
  readonly currency: CurrencyCode;
  /** Bu satırdan kesilen stopaj, kendi para biriminde. Yoksa "0". */
  readonly withheldTax: Decimal;
  /** Tutarı okunamadıysa true — satır toplama GİRMEDİ (`nakit_tutar_okunamadi`). */
  readonly unreadable: boolean;
}

/**
 * NAKİT AKIŞI — getirinin, parti defterinin göremediği bileşeni.
 *
 * ----------------------------------------------------------------------------
 * NİÇİN AYRI BİR BİLEŞEN, NİÇİN MALİYETTEN DÜŞÜLMÜYOR
 * ----------------------------------------------------------------------------
 *
 * Temettüyü açık partilerin maliyetinden düşmek de yaygın bir yöntem ve tek
 * bir "getiri" sayısı üretir. Burada seçilmedi, üç sebeple:
 *
 *  1. Parti defteri DEĞİŞMEZ kalır. `SPLIT`/`BONUS` dışında hiçbir şey
 *     partilere dokunmuyordu; temettü dokunsaydı G şeridinin değişmezleri
 *     (toplam maliyet sabit, birim maliyet türetilir) yeniden kurulmalıydı.
 *  2. VERGİ REJİMİ AYRI. Temettü stopajı ile değer artış kazancı aynı rejim
 *     değil; ikisini tek sayıda toplamak, vergi tarafını daha sonra
 *     ayrıştırılamaz hâle getirirdi.
 *  3. Maliyeti düşmek, uzun süre tutulan bir varlıkta maliyeti NEGATİFE
 *     düşürebilir ve "birim maliyet" anlamsızlaşır.
 *
 * Kullanıcı isterse iki sayıyı kendi toplar; `realizedReturn` bunu zaten
 * yapıyor. Ters yön — birleşmiş sayıdan bileşenleri geri çıkarmak — mümkün
 * değildi.
 *
 * ----------------------------------------------------------------------------
 * İŞARET KURALI
 * ----------------------------------------------------------------------------
 *
 * `income` ve `expense` İKİSİ DE POZİTİF büyüklüktür; `net = income −
 * expense`. Gideri negatif tutup toplamak da olurdu ama o zaman "masraf ne
 * kadar" sorusunun cevabı eksi işaretli bir sayı olurdu ve arayüz her yerde
 * mutlak değer almak zorunda kalırdı. Satır dökümündeki `amount` ise
 * İŞARETLİdir — orada soru "bakiyeye ne oldu".
 */
export interface CashFlow {
  /** Temettü + faiz, RAPOR para biriminde. Hiç yoksa null. */
  readonly income: StampedAmount | null;
  /** Masraf, RAPOR para biriminde, POZİTİF büyüklük. Hiç yoksa null. */
  readonly expense: StampedAmount | null;
  /** `income − expense`. Bileşenlerden biri çevrilemediyse null. */
  readonly net: StampedAmount | null;
  /** Dökümün tamamı — okunamayan satırlar da dâhil (`unreadable`). */
  readonly rows: readonly CashFlowRow[];
  /**
   * `CASH_FLOW_AMOUNT_FIELDS`teki her `null` tutarın SEBEBİ — motordan.
   *
   * Eskiden kart altı ve nakit dökümü bu üç tutarı sebepsiz bir "—" ile
   * gösteriyordu: masrafı olmayan varlığın gideri (`yok`, defterin normal
   * hâli) ile kuru bulunamayan temettünün geliri (`olculemedi`) AYNI
   * görünüyordu. Değişmez `PositionValuation.absent`inkiyle aynı: alan
   * `null` ise kaydı VAR, doluysa YOK (testte kilitli).
   */
  readonly absent: Readonly<Partial<Record<CashFlowAmountField, Absence>>>;
}

/** Nakit akışında sebebi söylenmek ZORUNDA olan tutarlar. */
export const CASH_FLOW_AMOUNT_FIELDS = ["income", "expense", "net"] as const;
export type CashFlowAmountField = (typeof CASH_FLOW_AMOUNT_FIELDS)[number];

/**
 * YAŞ VE YILLIK KARŞILIK (kullanıcı kararı 14.10.2026).
 *
 * "Bu para ortalama kaç gündür yatırımda ve bu sürede getirisi yılda yüzde
 * kaça denk geliyor" — bir mevduat faiziyle kıyaslanabilsin diye. YAKLAŞIK:
 * paranın giriş-çıkış zamanlaması tam izlenmiyor, maliyet ağırlıklı ortalama
 * bir yaşla özetleniyor.
 *
 * - `yasGun`: partilerin MALİYET ağırlıklı ortalama yaşı (rapor para
 *   biriminde, her parti kendi gününün kuruyla), en yakın tam güne.
 *   Varlıkta: elde duran partiler, alıştan sorulan güne. Toplamda: elde
 *   duranlar + satılmış partiler (alıştan satış gününe).
 * - `oran`: Varlıkta (gerçekleşmemiş kazanç + o varlığın net nakit akışı)
 *   ÷ elde duran maliyet — temettü dahil. Toplamda (gerçekleşmemiş +
 *   gerçekleşmiş + net nakit akışı) ÷ (açık + satılan maliyet) — "geçmiş
 *   dahil getiri" ile aynı tanım.
 * - `yillik`: BASİT yıllık karşılık, `oran × 365 ÷ yasGun` — bankaların
 *   mevduat faizini ilan ettiği biçim. `yasGun < YILLIK_KARSILIK_MIN_GUN`
 *   ise null (kısa sürede yıllığa çevirmek yanıltır).
 * - `absent`: null olan alanın sebebi (motordan).
 */
export interface YillikKarsilik {
  readonly yasGun: number | null;
  readonly oran: GainRatio | null;
  readonly yillik: Decimal | null;
  readonly absent: Readonly<Partial<Record<"yasGun" | "oran" | "yillik", Absence>>>;
}

/** Ağırlıklı yaşı bundan kısa olanda yıllık karşılık yazılmaz (kullanıcı, 14.10.2026). */
export const YILLIK_KARSILIK_MIN_GUN = 30;

/**
 * VARLIĞIN PAYI — SEÇİLİ KAPSAM içinde, değere göre (kullanıcı kararı
 * 15.10.2026, Varlık Dökümü "Pay" sütunu). Kapsam raporun kendisi: bütün
 * kasa, tek hesap ya da sanal hesap — ayrı bir "hesap payı" yok.
 *
 * Motor binde (tamsayı) olarak hesaplar ve kalan en büyük yöntemiyle
 * dağıtır: kapsamdaki payların toplamı TAM %100. Arayüz toplamaz, böler ya
 * da yuvarlamaz; yalnızca gösterir.
 */
export interface PayDegeri {
  /** Yüzde, en çok bir ondalık ("12.3", "0", "100"). */
  readonly yuzde: Decimal;
  /**
   * `false`: kapsamda DEĞERİ ÖLÇÜLEMEYEN açık pozisyon var; paylar yalnızca
   * ölçülebilenlerin toplamına göre (o varlığın payı sebebiyle "—").
   */
  readonly paydaTam: boolean;
}

export interface PositionValuation {
  readonly asset: AssetBinding;
  /** Açık partilerin toplamı (FIFO). Bölünme/bedelsiz UYGULANMIŞ. */
  readonly quantity: Decimal;
  /** Hareketlerin para birimi. */
  readonly currency: CurrencyCode;
  /**
   * Kullanılan birim fiyat. Kapalı pozisyonda, fiyat yoksa ya da elle TOPLAM
   * değer kullanıldıysa null — sebebi `absent.unitPrice`ta.
   */
  readonly unitPrice: StampedAmount | null;
  /** Piyasa değeri, FİYATIN para biriminde. */
  readonly marketValue: StampedAmount | null;
  /** Piyasa değeri, RAPOR para biriminde (kur damgası dâhil). */
  readonly reportValue: StampedAmount | null;
  /** FIFO maliyeti, partilerin kendi para biriminde. Karışıksa null. */
  readonly cost: StampedAmount | null;
  /** Maliyet, RAPOR para biriminde — her parti KENDİ tarihinin kuruyla. */
  readonly costConverted: StampedAmount | null;
  /** `reportValue − costConverted`. İkisinden biri yoksa null. */
  readonly unrealizedGain: StampedAmount | null;
  /**
   * `unrealizedGain ÷ (costConverted − temettüyle alınmış açık partilerin
   * maliyeti)` — paydası "cepten çıkan para" (kullanıcı kararı 19.10.2026;
   * gerçek ve sanal hesapta aynı). Payda yoksa ya da sıfırsa null; paydanın
   * kendisi `GainRatio.base`te.
   *
   * Paydası AÇIK PARTİLERİN maliyeti — satılmış partilerinki değil. Kapanmış
   * bir partinin maliyetini paydaya katmak, elde olmayan bir varlığın
   * getirisini elde olanınkine karıştırmak olurdu.
   */
  readonly unrealizedGainRatio: GainRatio | null;
  /** Gerçekleşmiş kazanç, RAPOR para biriminde — her satış KENDİ günüyle. */
  readonly realizedGain: StampedAmount | null;
  /**
   * SATILAN PARTİLERİN MALİYETİ, rapor para biriminde — her satış KENDİ
   * günüyle çevrilmiş, tıpkı kazancın kendisi gibi.
   *
   * Kendi başına da bir cevap ("bu varlığa girip çıktığım para ne kadardı")
   * ama asıl işi aşağıdaki iki oranın PAYDASI olmak. Tüketilen partilerden
   * biri maliyetsiz devirse null döner: eksik bir paydayla hesaplanan oran,
   * kazancı olduğundan büyük gösterir.
   */
  readonly soldCost: StampedAmount | null;
  /** `realizedGain ÷ soldCost`. Payda yoksa ya da sıfırsa null. */
  readonly realizedGainRatio: GainRatio | null;
  /**
   * NAKİT AKIŞI — temettü/faiz/masraf, her satış gibi her satır KENDİ
   * günüyle çevrilmiş. Bu varlıkta hiç nakit hareketi yoksa null.
   */
  readonly cashFlow: CashFlow | null;
  /**
   * GERÇEKLEŞMİŞ GETİRİ = `realizedGain + cashFlow.net`.
   *
   * Yalnızca İKİ bileşen de güvenilirse yayımlanıyor. "Güvenilir" demek var
   * demek DEĞİL: satışı olmayan bir varlıkta `realizedGain` yokluğu bir
   * eksiklik değil, sıfırdır. Ama satışı OLUP kuru bulunamadığı için
   * çevrilemeyen bir varlıkta bu sayı `null` döner — yarısı eksik bir
   * getiriyi "getiri" diye göstermek, göstermemekten kötüdür.
   */
  readonly realizedReturn: StampedAmount | null;
  /**
   * `realizedReturn ÷ soldCost`. Payda yoksa ya da sıfırsa null.
   *
   * PAYDANIN SEÇİMİ TARTIŞMALI VE BİLEREK BÖYLE. Pay iki bileşenli
   * (gerçekleşmiş kazanç + nakit akışı) ama nakit akışının kendine ait bir
   * maliyeti YOK: temettü, onu doğuran partinin maliyetiyle ölçülür. Satılan
   * partilerin maliyeti bu yüzden payda; alternatifi — nakit akışını
   * paydasız bırakıp orandan dışlamak — kullanıcının gördüğü "getiri"
   * sayısıyla oranının farklı şeyleri ölçmesi demek olurdu.
   *
   * Sonuç: hiç satış yapılmamış ama temettü almış bir varlıkta bu oran
   * null'dır. Bu bir eksiklik değil: paydası olmayan bir oran yoktur.
   */
  readonly realizedReturnRatio: GainRatio | null;
  /**
   * Kesilen vergi, RAPOR para biriminde. Kazanca DAHİL DEĞİL.
   * Satış stopajı ile temettü/faiz stopajının TOPLAMI.
   */
  readonly withheldTax: StampedAmount | null;
  /** Yaş ve yıllık karşılık — elde duranlar, temettü dahil (`YillikKarsilik`). */
  readonly yillikKarsilik: YillikKarsilik;
  /** Seçili kapsamdaki payı (`PayDegeri`); yoksa sebebi `absent.pay`ta. */
  readonly pay: PayDegeri | null;
  /**
   * Günlük getiri (`GunlukGetiri`, 22.10.2026). Yalnızca tek günlük rapor
   * isteğinde (`valuation()`) dolar; seri noktalarında yoktur. Hesaplanamadıysa
   * sebebi `gunlukYok`ta.
   */
  readonly gunluk?: GunlukGetiri;
  readonly gunlukYok?: Absence;
  /** Açık parti defteri — vergi sayısının denetlenebilirliği buna dayanıyor. */
  readonly openLots: readonly FifoLot[];
  /** Satış dökümü ("hangi partiden ne kadar"). */
  readonly sales: readonly FifoSale[];
  readonly notes: readonly ValuationNote[];
  /**
   * `POSITION_AMOUNT_FIELDS`teki her `null` tutarın sebebi. Dolu tutarın
   * kaydı YOK. Gerekçe `Absence` başlığında.
   */
  readonly absent: Readonly<Partial<Record<PositionAmountField, Absence>>>;
}

export interface ValuationOptions {
  /** Hangi güne değerlenecek. Verilmezse çağıranın "bugün"ü. */
  readonly asOf?: IsoDate;
  /** Rapor para birimi. Varsayılan "TRY". */
  readonly reportCurrency?: CurrencyCode;
  /**
   * Yalnızca bu hesaplar. Verilmezse tüm defter (yalnızca GERÇEK hesaplar).
   * Bir SANAL hesap (`sanalHesapMi`) yalnızca tek başına istenebilir;
   * hareketleri kuralından türetilir.
   */
  readonly accountIds?: readonly string[];
  /**
   * NAKİT TAKİBİ (kullanıcı kararı 14.10.2026: kasa düzeyinde kapatılabilir).
   * `false` → net nakit hareketi hesaplanmaz: `cashMovement` null, nakit
   * kovalarının damgaları üretilmez, `absent.cashMovementTotal` "kapalı"
   * der. Temettü/faiz/masraf GETİRİYE yine girer — o nakit takibi değil.
   * Verilmezse `true`. "Kendiliğinden" kararı (defterde yatırma/çekme var
   * mı) ayarı okuyan arayüzün; motor yalnızca açık/kapalı bilir.
   */
  readonly nakitTakibi?: boolean;
  /**
   * Kullanıcı nakit takibini AÇIKÇA kapattı (Ayarlar › Kapalı; "Otomatik"
   * değil). O zaman asıl getiri hesaplanmaz, sebebi "nakit izlenmiyor"
   * (kullanıcı kararı 19.10.2026). Otomatik'te asıl getiri yatırma/çekme
   * yazılmamış olsa da hesaplanır: eksik kalan alım bir para girişi sayılır.
   */
  readonly nakitKapaliSecildi?: boolean;
}

/**
 * GÜNLÜK SERİ (grafik; kullanıcı 12.10.2026).
 *
 * Her nokta, o gün için `valuation()`ın ürettiği raporun TOPLAMLARINDAN
 * türetiliyor — ikinci bir değerleme yok. Raporda olmayan iki sayı
 * ("geçmiş dahil getiri" ve oranı) motorun kendi tamsayı aritmetiğiyle,
 * motorda üretiliyor; arayüz para toplamaz.
 *
 * - `unrealizedGain` = mevcut getiri (değer − maliyet).
 * - `totalReturn` = mevcut getiri + gerçekleşmiş getiri (satış
 *   kazancı + temettü/faiz − masraf). Satış yapılınca çizgi kırılmaz.
 * - `unrealizedGainRatio`, `totalReturnRatio`: paydası "cepten çıkan para"
 *   (açık [+ satılan] maliyet − temettüyle alınmış partiler; 19.10.2026).
 *
 * Yokluk kuralı raporunkiyle aynı: `null` olan her alanın sebebi `absent`ta,
 * dolu alanın kaydı yok (testte kilitli).
 */
export const SERIES_FIELDS = [
  "value",
  "cost",
  "unrealizedGain",
  "unrealizedGainRatio",
  "totalReturn",
  "totalReturnRatio",
] as const;
export type SeriesField = (typeof SERIES_FIELDS)[number];

/** Bir isteğin kapsayabileceği en çok gün (her gün bir tam değerleme). */
export const SERIES_MAX_DAYS = 3700;

/**
 * İki "değişimin başı"nı birleştirir (`ChangedFrom`, device-api): daha erken
 * olan kazanır; "all" her şeyi yutar; `undefined` (bilinmiyor) "all" sayılır.
 */
export function mergeChangedFrom(
  a: IsoDate | "all" | null | undefined,
  b: IsoDate | "all" | null | undefined,
): IsoDate | "all" | null {
  if (a === undefined || b === undefined || a === "all" || b === "all") return "all";
  if (a === null) return b;
  if (b === null) return a;
  return a < b ? a : b;
}

export interface ValuationSeriesOptions {
  readonly from: IsoDate;
  readonly to: IsoDate;
  /** Varsayılan "TRY". */
  readonly reportCurrency?: CurrencyCode;
  readonly accountIds?: readonly string[];
}

export interface ValuationSeriesPoint {
  readonly date: IsoDate;
  readonly value: Decimal | null;
  readonly cost: Decimal | null;
  readonly unrealizedGain: Decimal | null;
  /** Ondalık kesir ("0.2" = %20). */
  readonly unrealizedGainRatio: Decimal | null;
  readonly totalReturn: Decimal | null;
  readonly totalReturnRatio: Decimal | null;
  /** O günün toplam varlığı, o güne göre bayat bir ölçüme dayanıyor. */
  readonly stale: boolean;
  readonly absent: Readonly<Partial<Record<SeriesField, Absence>>>;
}

export interface ValuationSeries {
  readonly from: IsoDate;
  readonly to: IsoDate;
  readonly reportCurrency: CurrencyCode;
  /** `from`dan `to`ya her takvim günü, sırayla. */
  readonly points: readonly ValuationSeriesPoint[];
}

// ---------------------------------------------------------------------------
// GETİRİ ÖZETİ — ana sayfa hesaplar kartı (kullanıcı kararı 13.10.2026)
// ---------------------------------------------------------------------------

/**
 * Üç oran, hepsi "geçmiş dahil getiri" (satış kazancı + temettü/faiz −
 * masraf + mevcut getiri) üzerinden:
 *
 *  - `toplam`: bugünün geçmiş dahil getiri oranı (Günlük Analiz'deki aynı
 *    sayı).
 *  - `sonYil`: son 365 günde oluşan getiri ÷ o günlerdeki portföy değerinin
 *    ortalaması (elde varlık olmayan gün 0 sayılır). Yıl içinde eklenen ya da
 *    çekilen para getiriyi şişirmez.
 *  - `gunluk`: bugünkü getiri değişimi ÷ dünkü portföy değeri.
 *
 * Girdi günlük seri (`valuationSeries` noktaları, bugün dahil son 366 gün);
 * motor yalnızca bölmeleri yapar. Ölçülemeyen oran `null` + sebep.
 */
export const GETIRI_OZETI_ALANLARI = ["toplam", "sonYil", "gunluk"] as const;
export type GetiriOzetiAlani = (typeof GETIRI_OZETI_ALANLARI)[number];

export interface GetiriOzeti {
  readonly tarih: IsoDate;
  readonly toplam: Decimal | null;
  readonly sonYil: Decimal | null;
  readonly gunluk: Decimal | null;
  readonly absent: Readonly<Partial<Record<GetiriOzetiAlani, Absence>>>;
}

// ---------------------------------------------------------------------------
// DÖNEMSEL ANALİZ — kayan periyot getirisi + sepet (kullanıcı, 13.10.2026)
// ---------------------------------------------------------------------------

/**
 * KAYAN PERİYOT GETİRİSİ. Aralıktaki HER TAKVİM GÜNÜ `d` için
 * `değer(d) ÷ değer(d − periyotGun) − 1`, rapor para biriminde. Ör. "son 3
 * yıl, SMH, 92 gün": her gün, o günde biten 92 günlük getiri.
 *
 * Değer = o günün (ya da öncesindeki son) kapanışı × o günün kuru; temettü
 * yeniden yatırılıyorsa hak kullanım günü kapanışıyla kesirli birim alınmış
 * sayılır. Bölünme HER ZAMAN düzeltilmiş kabul edilir (kaynak kapanışı
 * düzeltilmiş verir; kullanıcı kararı 13.10.2026).
 *
 * Hesap CİHAZDA (kullanıcı kararı 13.10.2026): merkez yalnızca fiyat, kur ve
 * şirket olayı verir; seçim ve ağırlıklar cihazdan çıkmaz.
 *
 * VERİ YOKSA SAYI YOK: bir günün getirisi ölçülemiyorsa `null`, sebebi
 * `bosluklar`da. Sıfır yazılmaz (kullanıcı kararı 13.10.2026).
 */
export const DONEMSEL_MAX_ARAC = 10;

/**
 * DÖVİZ KIYASI (kullanıcı kararı 13.10.2026): `kur:USD` = 1 USD'nin rapor
 * para birimindeki değeri. Merkezde ayrı bir araç YOK; cihazdaki kur
 * önbelleğinden hesaplanır (işlem günü = kurun yayın günü).
 */
export const KUR_KIYAS_ONEKI = "kur:";
export const KUR_KIYASLARI = [
  { para: "USD", ad: "Dolar / TL" },
  { para: "EUR", ad: "Euro / TL" },
] as const;
/** `from`..`to` en çok bu kadar gün (≈10 yıl). */
export const DONEMSEL_MAX_GUN = 3700;
export const DONEMSEL_MAX_PERIYOT_GUN = 1830;

/**
 * ORTALAMA FİYAT (kullanıcı kararı 13.10.2026): her günün değeri, o güne
 * kadarki son N İŞLEM GÜNÜNÜN (fiyat oluşmuş günler; hafta sonu ve tatil
 * sayılmaz) değerlerinin ortalaması. Her gün kendi kuruyla çevrilip ortalanır.
 */
export const DONEMSEL_MIN_ORTALAMA_GUN = 2;
export const DONEMSEL_MAX_ORTALAMA_GUN = 60;

/**
 * N işlem günü için geriye kaç TAKVİM günü fazladan bakılır (hafta sonları
 * ve tatiller için pay). Motor ve eşitleme AYNI sayıyı kullanır.
 */
export function ortalamaGeriGun(n: number): number {
  return Math.ceil((n * 7) / 5) + 10;
}

/**
 * SEPETİN ZAMAN İÇİNDEKİ DAVRANIŞI. `yok` = al ve tut: başlangıçta ağırlıkla
 * alınır, sonra ağırlıklar fiyatla kayar. Ötekiler: ayın / çeyreğin / yılın
 * ilk günü ağırlıklar hedefe geri çekilir.
 */
export const SEPET_DENGELEMELERI = ["yok", "aylik", "ceyreklik", "yillik"] as const;
export type SepetDengeleme = (typeof SEPET_DENGELEMELERI)[number];

export interface SepetAgirligi {
  readonly instrumentId: InstrumentId;
  /** Kesir ("0.4" = %40). Toplam tam 1 olmak ZORUNDA; negatif yok. */
  readonly agirlik: Decimal;
}

export interface SepetTanimi {
  readonly agirliklar: readonly SepetAgirligi[];
  readonly dengeleme: SepetDengeleme;
}

export interface DonemselAnalizIstegi {
  /** 1..`DONEMSEL_MAX_ARAC`, tekrarsız. */
  readonly instrumentIds: readonly InstrumentId[];
  readonly from: IsoDate;
  readonly to: IsoDate;
  readonly reportCurrency: CurrencyCode;
  /** 1..`DONEMSEL_MAX_PERIYOT_GUN`, takvim günü. */
  readonly periyotGun: number;
  readonly temettuYenidenYatir: boolean;
  /**
   * Ortalama fiyat: son N işlem günü (`DONEMSEL_MIN/MAX_ORTALAMA_GUN`).
   * Verilmezse ya da null ise gün sonu kapanışı.
   */
  readonly ortalamaGun?: number | null;
  /**
   * Verilirse sepet de hesaplanır ve BÜTÜN grafik en genç aracın ilk veri
   * gününden başlar (kullanıcı kararı 13.10.2026) — en genç SEPET ÜYESİNİN.
   * Ağırlıklar `instrumentIds`in bir ALT KÜMESİ, en az iki varlık:
   * kıyas serileri (BIST100, altın, döviz) sepete girmez, onunla kıyaslanır
   * (kullanıcı, 14.10.2026). Ağırlığı olmayan seçili varlık sepeti etkilemez.
   */
  readonly sepet?: SepetTanimi | null;
}

/**
 * SEPETİ HESAPLARDAN DOLDUR (kullanıcı, 15.10.2026).
 *
 * Seçilen hesaplar (gerçek ve `SANAL_HESAP_ONEKI`li sanal) `asOf` gününe
 * değerlenir; aynı araç birden çok hesapta ise değerleri toplanır. Oran =
 * aracın değeri ÷ dahil edilenlerin toplamı, RAPOR para biriminde.
 *
 * DIŞARIDA KALANLAR sebebiyle `disarida`da: elle değerlenen varlık (nakit,
 * fiziki altın…; Dönemsel Getiri yalnızca izlenen araçları çizer),
 * `izinli` listesinde olmayan ya da henüz hazırlanan araç, değeri ölçülemeyen
 * pozisyon, ve en büyük `DONEMSEL_MAX_ARAC` dışında kalanlar. Oranlar
 * dahil edilenler arasında yeniden ölçeklenir.
 *
 * Oran 4 haneli kesir ("0.3334" = %33,34); toplam TAM 1 (en büyük kalan
 * yöntemi). Liste değere göre büyükten küçüğe.
 */
export interface SepetDagilimiIstegi {
  readonly accountIds: readonly string[];
  readonly reportCurrency: CurrencyCode;
  /** Verilmezse bugün. */
  readonly asOf?: IsoDate;
  /** Dönemsel listede seçilebilen araçlar; dışındakiler `disarida`. */
  readonly izinli: readonly InstrumentId[];
}

export interface SepetDagilimi {
  readonly asOf: IsoDate;
  readonly reportCurrency: CurrencyCode;
  readonly agirliklar: readonly {
    readonly instrumentId: InstrumentId;
    /** Kesir, 4 hane. */
    readonly agirlik: Decimal;
    /** Seçilen hesaplardaki toplam değeri, rapor para biriminde. */
    readonly deger: Decimal;
  }[];
  /** Dahil edilenlerin toplam değeri; hiçbiri yoksa null. */
  readonly toplam: Decimal | null;
  readonly disarida: readonly { readonly ad: string; readonly sebep: MotorMesaji }[];
}

/** Bir serinin sayı üretemediği ardışık günler ve SEBEBİ. */
export interface DonemselBosluk {
  readonly from: IsoDate;
  readonly to: IsoDate;
  readonly absence: Absence;
}

export interface DonemselNokta {
  readonly date: IsoDate;
  /** Kesir ("0.0534" = %5,34). */
  readonly getiri: Decimal;
}

export interface DonemselOzet {
  /** Son ölçülebilen gün. */
  readonly son: DonemselNokta | null;
  readonly enDusuk: DonemselNokta | null;
  readonly enYuksek: DonemselNokta | null;
  /** Ölçülebilen günlerin aritmetik ortalaması. */
  readonly ortalama: Decimal | null;
  readonly olculenGun: number;
}

/** Bir periyodun bir ucundaki değer — karşılaştırıp denetleyebilmek için. */
export interface DonemselDeger {
  /** Değerin günü (periyot başı ya da sonu; takvim günü). */
  readonly date: IsoDate;
  /** Rapor para biriminde. Sepette endeks: kuruluş günü 100. */
  readonly deger: Decimal;
  /** Aracın KENDİ para birimindeki kapanış (sepette yok). */
  readonly fiyat?: Decimal;
  /** Kapanışın gerçek günü — son fiyat kuralıyla `date`ten önce olabilir. */
  readonly fiyatTarihi?: IsoDate;
  /** Temettüyle biriken birim (yalnızca 1'den farklıysa). */
  readonly birim?: Decimal;
  /**
   * Ortalama fiyat açıksa: ortalamaya giren işlem günlerinin ilki ve sonu ve
   * sayısı. `fiyat` o günlerin kapanışlarının ortalamasıdır.
   */
  readonly ortalama?: { readonly from: IsoDate; readonly to: IsoDate; readonly gun: number };
}

export interface DonemselUclar {
  readonly baslangic: DonemselDeger | null;
  readonly bitis: DonemselDeger | null;
}

/**
 * SIÇRAMA (kullanıcı kararı 13.10.2026): kapanışın ardışık iki işlem günü
 * arasında bir günde %40'tan fazla düşmesi ya da 5/3 katından fazla artması
 * (logaritmik olarak simetrik eşik). %100 bedelsiz fiyatı yarıya indirir;
 * kaynak bunu işlememişse grafikte sahte bir çöküş görünür. Merkezin
 * `jump_over_90pct` denetimi bunu yakalamaz (−%50 < %90). Veri DÜZELTİLMEZ,
 * işaretlenir.
 */
export interface DonemselSicrama {
  /** Sıçramanın görüldüğü işlem günü. */
  readonly date: IsoDate;
  /** Önceki işlem günü ve kapanışı (aracın kendi para biriminde). */
  readonly oncekiTarih: IsoDate;
  readonly oncekiFiyat: Decimal;
  readonly fiyat: Decimal;
  /** Kesir ("-0.5" = −%50). */
  readonly degisim: Decimal;
  /**
   * Kaynakta ±3 gün içinde bölünme/bedelsiz kaydı var mı. `null` = aracın
   * olay bilgisi yok (sorulamadı ≠ yok).
   */
  readonly bilinenOlay: boolean | null;
}

export interface DonemselSeri {
  /** Araç kimliği; sepet için `"sepet"`. */
  readonly kimlik: string;
  /** Aracın kendi para birimi (künyeden); sepette ve bilinmiyorsa null. */
  readonly paraBirimi: CurrencyCode | null;
  /** İlk değerlenebilen gün (geriye bakış dahil); hiç yoksa null. */
  readonly ilkVeri: IsoDate | null;
  /** `from`dan `to`ya HER TAKVİM GÜNÜ, sırayla; ölçülemeyen gün `null`. */
  readonly getiriler: readonly (Decimal | null)[];
  /** `getiriler` ile aynı hizada: o günün periyot başı ve sonu değerleri. */
  readonly uclar: readonly DonemselUclar[];
  readonly bosluklar: readonly DonemselBosluk[];
  /** Geriye bakış dahil aralıktaki olağan dışı fiyat sıçramaları (sepette boş). */
  readonly sicramalar: readonly DonemselSicrama[];
  readonly ozet: DonemselOzet;
  /**
   * Seri düzeyinde bilgi (ör. "temettü bilgisi 2019 öncesi için yok"). Her
   * not bir anormallik; olağan durum not üretmez.
   */
  readonly notlar: readonly MotorMesaji[];
}

export interface DonemselAnalizSonucu {
  /** ÇİZİLEN aralık. Sepet varken istenenden geç başlayabilir. */
  readonly from: IsoDate;
  readonly to: IsoDate;
  readonly istenenFrom: IsoDate;
  readonly reportCurrency: CurrencyCode;
  readonly periyotGun: number;
  readonly temettuYenidenYatir: boolean;
  /** Ortalama fiyat gün sayısı; null = gün sonu kapanışı. */
  readonly ortalamaGun: number | null;
  /** İstekteki sırayla, her araç için bir seri. */
  readonly seriler: readonly DonemselSeri[];
  readonly sepet: DonemselSeri | null;
  /** Sepetin kurulduğu gün (en genç aracın ilk günü ya da geriye bakışın başı). */
  readonly sepetKurulus: IsoDate | null;
}

// ---------------------------------------------------------------------------
// SANAL HESAP — geriye dönük deneme (back-test; kullanıcı kararları 14.10.2026,
// `claude/notlar/2026-10-14-back-test-kararlar.md`)
// ---------------------------------------------------------------------------

/**
 * SANAL HESAP = KURAL. Saklanan şey yalnızca kuraldır (profilin şifreli
 * kabında, `settings` içinde; yedeğe ve eşitlemeye girer). Hareketleri
 * kuraldan HER SEFERİNDE yeniden türetilir, deftere YAZILMAZ ve elle
 * düzenlenemez. Değerleme gerçek hesaplarınkiyle AYNI motordur
 * (`valuation`, `valuationSeries`); ikinci motor yok.
 *
 * "BÜTÜN DEFTER" = GERÇEK HESAPLAR. Sanal hesap ancak `accountIds` içinde
 * TEK BAŞINA istenince değerlenir; gerçek hesaplarla karıştırılamaz.
 *
 * GELECEĞE BAKMA YOK: bir günün kararı (alım bölüşümü, dengeleme satışı)
 * yalnızca o gün kapanışında bilinenle verilir ve o kapanıştan uygulanır.
 *
 * VERGİ YOK, KUR MAKASI YOK (kullanıcı kararı 14.10.2026); komisyon var.
 */
export const SANAL_HESAP_ONEKI = "sanal:";
export const SANAL_MAX_VARLIK = 10;
export const SANAL_MAX_HESAP = 20;
/** Alım gününe rastlantısal kayma, her yön için en çok. */
export const SANAL_MAX_KAYMA_GUN = 15;
export const SANAL_MAX_PERIYOT_ADIM = 366;

export function sanalHesapMi(accountId: string): boolean {
  return accountId.startsWith(SANAL_HESAP_ONEKI);
}

export const SANAL_PERIYOT_BIRIMLERI = ["gun", "hafta", "ay"] as const;
export type SanalPeriyotBirimi = (typeof SANAL_PERIYOT_BIRIMLERI)[number];

/**
 * DENGELEME (kullanıcı kararı 14.10.2026):
 *  - `yok`: her alımda para hedef oranlarla bölünür; dengeleme yok.
 *  - `alimla`: satış yok. Alım parası sırayla: hedef oranı en büyük olan
 *    varlık önce; eşitse hedefinden en çok geride kalan; o da eşitse ad
 *    (harf) sırası. Her biri hedefine çıkacak kadar alır, para yettiğince;
 *    herkes hedefindeyse kalan hedef oranlarla bölünür.
 *  - `satarak`: alım günü, kilitli OLMAYAN ve hedefini `dengelemeEsigi`nden
 *    fazla (yüzde puanı) aşan varlık hedefine inecek kadar satılır; elde
 *    edilen para alım parasına katılır ve `alimla` sırasıyla dağıtılır.
 *    Kilitli varlık hiç satılmaz.
 */
export const SANAL_DENGELEMELERI = ["yok", "alimla", "satarak"] as const;
export type SanalDengeleme = (typeof SANAL_DENGELEMELERI)[number];

/**
 * Temettü: aynı varlığa geri yatırılır, bir sonraki alıma eklenir ya da YOK
 * SAYILIR (kullanıcı kararı 19.10.2026): `yokSay`da kuraldan temettü hareketi
 * türetilmez, sonuç fiyat getirisidir; temettü notları da motorda bastırılır.
 * Kayıtlı kurallar ve varsayılan (`ayniVarlik`) değişmez.
 */
export const SANAL_TEMETTU_KULLANIMLARI = ["ayniVarlik", "sonrakiAlim", "yokSay"] as const;
export type SanalTemettuKullanimi = (typeof SANAL_TEMETTU_KULLANIMLARI)[number];

/**
 * KÜSURAT VARSAYILANI (kullanıcı kararı 14.10.2026): kripto, döviz, emtia,
 * yabancı hisse ve yabancı ETF küsuratlı alınabilir; BIST hissesi/ETF'si ve
 * TEFAS fonu tam adet. Kullanıcı varlık başına değiştirebilir; YALNIZCA
 * varsayılandan farklı seçim kaydedilir.
 */
export function kesirliVarsayilan(assetClass: string | null, currency: CurrencyCode | null): boolean {
  switch (assetClass) {
    case "crypto":
    case "fx":
    case "commodity":
    case "equity_foreign":
      return true;
    case "etf":
      return currency !== null && currency !== "TRY";
    default:
      return false;
  }
}

export interface SanalVarlik {
  readonly instrumentId: InstrumentId;
  /** Hedef oran, kesir ("0.4" = %40). Toplam tam 1. */
  readonly oran: Decimal;
  /** Yalnızca varsayılandan (`kesirliVarsayilan`) farklıysa yazılır. */
  readonly kesirli?: boolean;
  /** `satarak` dengelemede hiç satılmaz. */
  readonly kilitli?: boolean;
}

export interface SanalPeriyot {
  readonly birim: SanalPeriyotBirimi;
  /** "X günde/haftada/ayda 1": 1..`SANAL_MAX_PERIYOT_ADIM`. */
  readonly adim: number;
}

/**
 * PARA AKIŞI.
 *  - `duzenli`: başlangıçta `baslangicTutari` (isteğe bağlı) + her periyotta
 *    `periyotTutari`; masraflar dahil aşılamaz. `cekim` verilirse onun
 *    başlangıcından itibaren aynı takvimle çekim yapılır ve düzenli alım
 *    durur.
 *  - `defter` (B senaryosu): seçilen GERÇEK hesapların para girişleri ve
 *    çıkışları aynı gün, aynı tutarla (hesap para birimine o günün kuruyla
 *    çevrilerek). Yatırma/çekme kaydı yoksa alışların (masraf dahil) ve
 *    satışların (masraf düşülmüş) tutarı kullanılır. Kayma uygulanmaz.
 */
export type SanalParaAkisi =
  | {
      readonly tur: "duzenli";
      readonly periyot: SanalPeriyot;
      readonly baslangicTutari?: Decimal | null;
      readonly periyotTutari: Decimal;
      readonly cekim?: { readonly tutar: Decimal; readonly baslangic: IsoDate } | null;
    }
  | { readonly tur: "defter"; readonly accountIds: readonly string[] };

export interface SanalHesapTanimi {
  /** `SANAL_HESAP_ONEKI` + kimlik. */
  readonly id: string;
  readonly ad: string;
  readonly renk?: HesapRengi;
  /** Para akışının ve nakdin para birimi (`CURRENCIES`). */
  readonly paraBirimi: CurrencyCode;
  readonly varliklar: readonly SanalVarlik[];
  readonly baslangic: IsoDate;
  /** Verilmezse açık uçlu: hesap zamanla ilerler. */
  readonly bitis?: IsoDate | null;
  readonly paraAkisi: SanalParaAkisi;
  /**
   * KOMİSYON (kullanıcı kararları 15.10.2026, hesap başına tek kural):
   * işlem başına `brüt × komisyonOrani + komisyonSabit`, sonra en az
   * `komisyonEnAz`, en çok `komisyonEnCok`. Alış ve satışta. Örnekler:
   * yalnızca yüzde; yalnızca sabit (Midas 1,5 $); yüzde + en az (%0,1, en az
   * 0,75 $). Sabit/en az/en çok `komisyonParaBirimi` cinsindendir (yoksa
   * hesabın para birimi) ve işlem günü varlığın para birimine çevrilir.
   */
  readonly komisyonOrani: Decimal;
  readonly komisyonSabit?: Decimal | null;
  readonly komisyonEnAz?: Decimal | null;
  readonly komisyonEnCok?: Decimal | null;
  readonly komisyonParaBirimi?: CurrencyCode | null;
  /** Temettü aynı varlığa yatırılırken de komisyon alınır mı (yoksa evet). */
  readonly temettuKomisyonu?: boolean;
  /**
   * Alımda artan (bir adete yetmeyen, komisyondan kalan) nakit bir sonraki
   * alıma eklenir mi (yoksa evet). Hayırsa nakitte BEKLER, yalnızca çekimde
   * kullanılır. Temettünün "sonraki alıma" eklenmesi bundan bağımsızdır.
   */
  readonly artanSonrakiAlima?: boolean;
  readonly temettu: SanalTemettuKullanimi;
  readonly dengeleme: SanalDengeleme;
  /** `satarak`ta zorunlu: kesir ("0.03" = 3 puan). */
  readonly dengelemeEsigi?: Decimal | null;
  /** Periyodik işlem gününe rastlantısal kayma: [−geri, +ileri] gün. */
  readonly kayma: { readonly geri: number; readonly ileri: number };
  /** Rastlantının tohumu; aynı tohum her cihazda aynı hareketleri verir. */
  readonly tohum: number;
}

/** Kaydederken kimlik verilmezse yeni hesaptır; tohum verilmezse üretilir. */
export type SanalHesapGirdisi = Omit<SanalHesapTanimi, "id" | "tohum"> & {
  readonly id?: string;
  readonly tohum?: number;
};

/** Üreticinin söylediği bir şey (alım ertelendi, temettü bilgisi yok…). */
export interface SanalNot {
  readonly tarih: IsoDate | null;
  readonly metin: MotorMesaji;
  /** `uyari`: sonuç bundan etkilendi. `bilgi`: yalnızca açıklama. */
  readonly ton: "uyari" | "bilgi";
}

export interface SanalHareketDokumu {
  readonly id: string;
  /** Hareketlerin üretildiği son gün. */
  readonly asOf: IsoDate;
  readonly hareketler: readonly Transaction[];
  /** Alımda kullanılmayıp bekleyen nakit, hesap para biriminde. */
  readonly bekleyenNakit: Decimal;
  /** Üretilemediyse sebebi (ör. başlangıç bir varlığın verisinden önce). */
  readonly hata: MotorMesaji | null;
  readonly notlar: readonly SanalNot[];
}

export interface SanalHesapPort {
  liste(): Promise<readonly SanalHesapTanimi[]>;
  kaydet(girdi: SanalHesapGirdisi): Promise<SanalHesapTanimi>;
  sil(id: string): Promise<void>;
  /** Kuraldan türetilen hareketler (salt okunur); `asOf` verilmezse bugün. */
  hareketler(id: string, asOf?: IsoDate): Promise<SanalHareketDokumu>;
}

// ---------------------------------------------------------------------------
// ÖLÇÜLER — risk ve dönem ölçüleri (kullanıcı kararı 14.10.2026; gerçek ve
// sanal hesaplar için aynı)
// ---------------------------------------------------------------------------

/**
 * Girdi günlük seri (`valuationSeries` noktaları); motor yalnızca bölme ve
 * zincirleme yapar. GÜNLÜK GETİRİ, ana sayfa getiri özetindeki tanımla aynı:
 * o günün geçmiş dahil getiri değişimi ÷ önceki günün toplam varlığı. Para
 * yatırmak/çekmek getiri sayılmaz. Günler zincirlenerek (zaman ağırlıklı)
 * dönem getirileri kurulur.
 *
 *  - `getiri`: aralığın zincirlenmiş getirisi.
 *  - `enBuyukDusus`: zincirlenmiş endeksin zirveden dibe en büyük kaybı.
 *  - `enIyiYil` … `enKotuAy`: takvim yılı/ayı; aralığa kısmen giren dönem
 *    `tam: false` ile işaretli.
 *  - `oynaklik`: günlük getirilerin standart sapması × √365 (takvim günü).
 *  - `kiyas`: seçilen kıyas serisinin aynı aralıktaki fiyat getirisi ve
 *    fark (getiri − kıyas getirisi).
 *
 * Risksiz faize göre ölçüler (Sharpe vb.) YOK: mevduat serisi yok, oran
 * uydurulmaz.
 */
export const OLCU_ALANLARI = [
  "getiri",
  "enBuyukDusus",
  "enIyiYil",
  "enKotuYil",
  "enIyiAy",
  "enKotuAy",
  "oynaklik",
  "kiyas",
] as const;
export type OlcuAlani = (typeof OLCU_ALANLARI)[number];

export interface OlcuDonem {
  /** "2024" ya da "2024-03". */
  readonly donem: string;
  readonly oran: Decimal;
  /** Dönemin tamamı aralıkta ve ölçülmüş mü. */
  readonly tam: boolean;
}

export interface OlcuDusus {
  readonly oran: Decimal;
  readonly zirve: IsoDate;
  readonly dip: IsoDate;
  /** Zirve seviyesine dönülen gün; dönülmediyse null. */
  readonly toparlanma: IsoDate | null;
}

export interface OlcuKiyas {
  readonly instrumentId: InstrumentId;
  readonly getiri: Decimal;
  readonly fark: Decimal;
}

export interface OlculerIstegi {
  readonly noktalar: readonly ValuationSeriesPoint[];
  readonly reportCurrency: CurrencyCode;
  /** Kıyas serisi (fiyatları önceden eşitlenmiş olmalı). */
  readonly kiyas?: InstrumentId | null;
}

export interface Olculer {
  readonly from: IsoDate;
  readonly to: IsoDate;
  readonly getiri: Decimal | null;
  readonly enBuyukDusus: OlcuDusus | null;
  readonly enIyiYil: OlcuDonem | null;
  readonly enKotuYil: OlcuDonem | null;
  readonly enIyiAy: OlcuDonem | null;
  readonly enKotuAy: OlcuDonem | null;
  readonly oynaklik: Decimal | null;
  readonly kiyas: OlcuKiyas | null;
  /** Günlük getirisi ölçülebilen gün sayısı. */
  readonly olculenGun: number;
  /**
   * Ölçülemeyen (değer ya da getirisi "ölçülemedi" olan) gün sayısı. Bu
   * günler endekste ATLANIR; sıfırdan büyükse bütün ölçüler kısmidir ve
   * `not` sebebini söyler.
   */
  readonly olculemeyenGun: number;
  readonly not: MotorMesaji | null;
  readonly absent: Readonly<Partial<Record<OlcuAlani, Absence>>>;
}

/**
 * PORTFÖY RAPORU.
 *
 * `excluded` bu yapının namus bekçisi: değerlenemeyen bir pozisyon
 * toplamdan SESSİZCE düşmez, sebebiyle birlikte listelenir. `complete`
 * bunun tek satırlık hâli ve arayüz "toplam" kelimesini ancak o doğruyken
 * niteliksiz kullanabilir.
 */
/**
 * NET NAKİT HAREKETİ — "elimde ne kadar nakit var" sorusuna verilebilecek
 * DÜRÜST cevap.
 *
 * ----------------------------------------------------------------------------
 * NİÇİN "BAKİYE" DEĞİL "NET HAREKET"
 * ----------------------------------------------------------------------------
 *
 * Bakiye, deftere yazılmamış bir açılış tutarını da içerir. Parafon defteri
 * kullanıcının yazdığı satırlardan ibaret ve çoğu defter alışla başlar —
 * o alışı fonlayan yatırma kaydı yoktur. Böyle bir defterde türetilen sayı
 * NEGATİFTİR.
 *
 * İki cevap vardı ve ikisi de aynı sayıyı üretir; farkı ne İDDİA ettikleri:
 *
 *   "bakiye"     → "elinde şu kadar para var" — defter eksikse YANLIŞ.
 *   "net hareket"→ "bu defterin satırları şu kadar nakit hareketi anlatıyor"
 *                  — defter eksik olsa da DOĞRU.
 *
 * İkincisi seçildi. Negatif bir sonuç böylece bir kusur değil bir bilgidir:
 * "deftere girmemiş bir para kaynağı var". Kullanıcı açılış tutarını bir
 * `DEPOSIT` satırı olarak yazdığında aynı sayı kendiliğinden gerçek bakiyeye
 * dönüşür — ek bir alan, ek bir şema, ek bir göç gerekmeden.
 *
 * Bu yüzden NEGATİFLİK DAMGALANMIYOR: §"ekranda görünen her işaret bir
 * anormallik demektir" kuralı gereği, beklenen bir durumu işaretlemek gerçek
 * anormallikleri kalabalıkta kaybeder.
 *
 * ----------------------------------------------------------------------------
 * TOPLAM DEĞERE KATILMIYOR
 * ----------------------------------------------------------------------------
 *
 * `totalValue` bugün denetlenebilir bir sayı: değerlenebilen pozisyonların
 * toplamı, dışlananları sebebiyle listeli. Eksik olabilen bir nakit sayısını
 * onun içine katmak, doğru bir sayıyı şüpheli hâle getirirdi. Nakit kendi
 * başına duruyor; ikisini toplamak isteyen kullanıcı iki sayıyı da görüyor.
 */
export interface CashMovementByType {
  readonly type: TransactionType;
  readonly count: number;
  /** `CASH_SIGN` uygulanmış toplam, KOVANIN para biriminde. İşaretli. */
  readonly amount: Decimal;
}

/**
 * Bir (hesap, para birimi) kovası. Ayrım hesapta DA olmak zorunda: "ne kadar
 * nakdim var" sorusunun pratik cevabı hesap bazındadır, çünkü para bir
 * hesaptan ötekine kendiliğinden geçmez.
 */
export interface CashMovementBucket {
  readonly accountId: string;
  readonly currency: CurrencyCode;
  /**
   * Net — KOVANIN kendi para biriminde. Okunamayan satır varsa `null`
   * (`nakit_hareketi_okunamadi`); kırılım yine de dolu kalır.
   */
  readonly net: StampedAmount | null;
  /**
   * Net, RAPOR para biriminde.
   *
   * KUR `asOf` GÜNÜNÜNKÜ ve bu, raporun geri kalanından BİLEREK farklı bir
   * kural. Maliyet ve gerçekleşmiş kazanç her satırı KENDİ gününün kuruyla
   * çeviriyor çünkü onlar AKIŞ: her satır kendi anında olmuş bitmiş bir
   * olay. Bakiye ise STOK — bugün elde duran para. Bugünkü parayı geçmiş
   * günlerin kurlarıyla çevirmek, hiç yapılmamış bir bozdurma dizisini
   * varsaymak olurdu.
   */
  readonly netConverted: StampedAmount | null;
  /**
   * Tür kırılımı — "bu sayı nereden çıktı"nın cevabı.
   *
   * SATIR SATIR DÖKÜM TAŞINMIYOR: `CashFlow.rows` bir VARLIĞIN birkaç
   * temettüsüdür, bu ise defterin TAMAMIDIR (1.700 hareket). Kırılım en çok
   * 12 satır ve aynı soruyu yanıtlıyor. Bozuk satırın kimliği `unreadableIds`
   * ile ayrıca veriliyor — kullanıcı düzeltilecek kaydı bulabilmeli.
   */
  readonly byType: readonly CashMovementByType[];
  /** Tutarı okunamayan satırların kimlikleri. Boşsa net güvenilir. */
  readonly unreadableIds: readonly string[];
}

export interface CashMovement {
  readonly buckets: readonly CashMovementBucket[];
  /**
   * Bütün kovaların RAPOR para birimindeki toplamı, `asOf` kuruyla.
   * Bir kova bile çevrilemezse `null` — eksik toplam yayımlanmaz.
   */
  readonly total: StampedAmount | null;
  /** Toplama GİREMEYEN kovalar, sebebiyle. `PortfolioReport.excluded` deseni. */
  readonly excluded: readonly {
    readonly accountId: string;
    readonly currency: CurrencyCode;
    readonly reason: MotorMesaji;
  }[];
  /** `excluded.length === 0` ile eşdeğer; testte kilitli. */
  readonly complete: boolean;
}

/**
 * ASIL GETİRİ VE ASIL ORAN (kullanıcı kararı 19.10.2026): portföye giren
 * paraya göre ne kazanıldı. Satıştan gelen parayla yapılan alım yeni maliyet
 * sayılmaz; yalnızca portföye giren para sayılır.
 *
 *   asıl getiri = bugünkü değer (nakit dahil) − (yatırılan − çekilen)
 *   asıl oran   = asıl getiri ÷ (yatırılan − çekilen)
 *
 * YATIRILAN: yatırma hareketleri + "dışarıdan" ödenen vergi + eldeki nakdin
 * yetmediği alımların eksik kısmı (`ortukGiris`; hesap × para birimi
 * kovasında, gün gün yürünerek; aynı gün önce girişler). Her giriş ve çekiş
 * KENDİ gününün kuruyla, nakit bakiyesi sorulan günün kuruyla çevrilir.
 *
 * YILLIK: para ağırlıklı — getiri × 365 ÷ Σ(giriş × girişten bugüne gün)
 * (çekişler eksiyle). Yaş = para-gün ÷ net yatırılan; 30 günden gençse yazılmaz.
 *
 * Terimler motorun; arayüz yalnızca yazar. `null` olanın sebebi `absent`ta.
 */
export interface AsilGetiri {
  /** Pozisyonların değeri + nakit bakiyesi. */
  readonly deger: StampedAmount | null;
  /** Nakit bakiyesi (örtük girişlerden sonra), sorulan günün kuruyla. */
  readonly nakit: StampedAmount | null;
  /** Açık yatırma + dışarıdan vergi. */
  readonly yatirilan: StampedAmount | null;
  /** Eldeki nakdin yetmediği alımların eksik kısmı (para girişi sayıldı). */
  readonly ortukGiris: StampedAmount | null;
  readonly cekilen: StampedAmount | null;
  /** yatırılan + örtük giriş − çekilen. */
  readonly netYatirilan: StampedAmount | null;
  readonly getiri: StampedAmount | null;
  readonly oran: GainRatio | null;
  /** Para ağırlıklı yıllık karşılık (oran, ondalık kesir). */
  readonly yillik: Decimal | null;
  /** Para ağırlıklı yaş, gün. */
  readonly yasGun: number | null;
  readonly absent: Readonly<Partial<Record<"getiri" | "oran" | "yillik", Absence>>>;
}

/**
 * GÜNLÜK GETİRİ (Gelen kararlar 22.10.2026) — ana sayfadaki günlük getiriyle
 * aynı tanım: bugünkü geçmiş dahil getiri − bir önceki günün geçmiş dahil
 * getirisi; yüzde, bir önceki günün değerine bölünür. O gün yapılan alım ve
 * satımın tutarı farka girmez (getiriyi değiştirmezler); masrafı girer.
 *
 * `bayat`: bugünün değeri bayat bir fiyata dayanıyor (ör. fon fiyatı henüz
 * açıklanmadı). Sayı yine verilir; ekran uyarı işaretiyle gösterir. Hükmü
 * değerleme motorunun bayatlık damgasıdır.
 *
 * `kurEtkisi`: yabancı para birimli varlıkta farkın kur değişiminden gelen
 * kısmı = bugünkü değer − (bugünkü değerin dünkü kurla karşılığı). Rapor para
 * biriminde fiyatlanan varlıkta null.
 */
export interface GunlukGetiri {
  /**
   * Gösterilen değişimin günü. Normalde sorulan gün. HAREKETSİZ GÜNDE
   * (kullanıcı kararı 25.10.2026): varlığın bugün değeri hiç değişmediyse
   * (fiyat günü dünküyle aynı, kur da oynamamış) son fiyat değişiminin günü
   * ve `sonHareketGunu: true`. Rapor toplamında: bugün hiçbir varlık
   * değişmediyse son hareketli gün; aksi hâlde bugün.
   */
  readonly gun: IsoDate;
  readonly sonHareketGunu: boolean;
  /**
   * Yalnızca rapor toplamında: toplam bugünün değişimi ama bazı satırlar son
   * hareket günlerini gösteriyor — satırların toplamı kutuyu tutmaz.
   */
  readonly satirlarKarisik?: boolean;
  /** Karşılaştırılan önceki gün (`gun`den bir takvim günü önce). */
  readonly oncekiGun: IsoDate;
  /** Formülün terimleri: bugünkü ve önceki günün geçmiş dahil getirisi. */
  readonly bugunGetiri: StampedAmount;
  readonly oncekiGetiri: StampedAmount;
  /** `bugunGetiri − oncekiGetiri`. */
  readonly tutar: StampedAmount;
  /**
   * `tutar ÷ önceki günün değeri` (`base` = önceki günün değeri). Önceki gün
   * değer yoksa null, sebebi `oranYok`ta.
   */
  readonly oran: GainRatio | null;
  readonly oranYok?: Absence;
  readonly kurEtkisi: StampedAmount | null;
  readonly bayat: boolean;
}

export interface PortfolioReport {
  /** SORULAN gün. Rapordaki tüm `staleDays` buna göre. */
  readonly date: IsoDate;
  readonly reportCurrency: CurrencyCode;
  readonly positions: readonly PositionValuation[];
  /** Değerlenebilen pozisyonların toplamı. Hiçbiri değerlenemediyse null. */
  readonly totalValue: StampedAmount | null;
  readonly totalCost: StampedAmount | null;
  readonly totalUnrealizedGain: StampedAmount | null;
  /** `totalUnrealizedGain ÷ (totalCost − temettüyle alınmış açık partiler)` — "cepten çıkan para". */
  readonly totalUnrealizedGainRatio: GainRatio | null;
  readonly totalRealizedGain: StampedAmount | null;
  /** Satılan partilerin maliyeti — iki gerçekleşmiş oranın paydası. */
  readonly totalSoldCost: StampedAmount | null;
  /** `totalRealizedGain ÷ totalSoldCost`. */
  readonly totalRealizedGainRatio: GainRatio | null;
  /**
   * Nakit akışı toplamı. `rows` BURADA BOŞ: döküm pozisyon satırlarında
   * duruyor ve portföy düzeyinde yeniden taşımak raporu iki katına
   * çıkarırdı (1.700 hareketlik defterde bu ucuz değil).
   */
  readonly totalCashFlow: CashFlow | null;
  /** `totalRealizedGain + totalCashFlow.net` — ikisi de güvenilirse. */
  readonly totalRealizedReturn: StampedAmount | null;
  /** `totalRealizedReturn ÷ totalSoldCost`. */
  readonly totalRealizedReturnRatio: GainRatio | null;
  /**
   * GERİ YATIRILAN TEMETTÜ MALİYETİ (kullanıcı kararı 19.10.2026): temettüyle
   * alınmış partilerin maliyeti — açık olanlar kendi günlerinin kuruyla, satılmış
   * olanlar satış gününün kuruyla (açık maliyet ve satılan maliyetle AYNI kural).
   * "Geçmiş dahil getiri %"nin paydasından ("cepten çıkan para") düşülür.
   * Temettüyle alınmış parti yoksa null.
   */
  readonly totalGeriYatirim: StampedAmount | null;
  readonly totalWithheldTax: StampedAmount | null;
  /** Toplama GİREMEYEN pozisyonlar — sessiz eksik toplam olmaz. */
  readonly excluded: readonly { readonly asset: AssetBinding; readonly reason: MotorMesaji }[];
  /** `excluded.length === 0` ile eşdeğer; testte kilitli. */
  readonly complete: boolean;
  /**
   * NET NAKİT HAREKETİ — hesap × para birimi kırılımıyla. Defterde hiç
   * hareket yoksa ya da nakit takibi kapalıysa (`ValuationOptions.nakitTakibi`)
   * `null`; hangisi olduğunu `absent.cashMovementTotal` söyler.
   *
   * `totalValue`ya KATILMIYOR ve katılmamalı; gerekçesi `CashMovement`
   * başlığında.
   */
  readonly cashMovement: CashMovement | null;
  /** Yaş ve yıllık karşılık — her şey dahil (`YillikKarsilik`). */
  readonly yillikKarsilik: YillikKarsilik;
  /** Asıl getiri ve asıl oran — portföye giren paraya göre (19.10.2026). */
  readonly asilGetiri: AsilGetiri;
  /** Kapsamın toplam günlük getirisi (`GunlukGetiri`); yoksa sebebi `gunlukYok`ta. */
  readonly gunluk?: GunlukGetiri;
  readonly gunlukYok?: Absence;
  readonly notes: readonly ValuationNote[];
  /** `REPORT_TOTAL_FIELDS`teki her `null` toplamın sebebi — özet kartları için. */
  readonly absent: Readonly<Partial<Record<ReportTotalField, Absence>>>;
}

// ---------------------------------------------------------------------------
// FORMÜL SÖZLÜĞÜ (kullanıcı kararı 19.10.2026)
// ---------------------------------------------------------------------------

/**
 * Başlıkların yanındaki ⓘ balonu: her değerin NASIL hesaplandığı. TEK
 * sözlük; anahtarlar burada, metin arayüzün dil sözlüğünde (`M.formul`,
 * çok dilli arayüz 27.10.2026) — her dilde aynı anahtar, aynı anlam. Sayının
 * kendi balonundaki "bu değerle yazılmış hâli" de aynı terimleri kullanır
 * (terimlerin tutarları rapordan, arayüz yeniden toplamaz).
 *
 * `temettuHaric`: sanal hesap kuralı temettüyü yok sayıyorsa seçime uygun
 * ikinci metin. Listede olmayan formül o seçimde de aynıdır.
 */
export const DEGER_FORMULU_KODLARI = [
  "piyasaDegeri",
  "pay",
  "maliyet",
  "mevcutGetiri",
  "gerceklesmisKazanc",
  "nakitAkisi",
  "gerceklesmisGetiri",
  "yillikKarsilik",
  "asilGetiri",
  "gunlukGetiri",
  "kesilenVergi",
  // Varlık Dökümü yeni kart seti (gelen karar 02.11.2026).
  "toplamMaliyet",
  "toplamVarlik",
  "getiri",
  "mevduatKarsiligi",
] as const;

export type DegerFormuluKodu = (typeof DEGER_FORMULU_KODLARI)[number];

/** Temettü yok sayılınca metni değişen formüller. */
export const TEMETTU_HARIC_FORMULLER = ["mevcutGetiri", "nakitAkisi", "gerceklesmisGetiri"] as const satisfies readonly DegerFormuluKodu[];

/** Bir formülün metni (dil sözlüğündeki biçim). */
export interface DegerFormulu {
  /** Başlığın tam adı (balonun ilk satırı). */
  readonly ad: string;
  readonly formul: string;
  readonly temettuHaric?: string;
}

// ---------------------------------------------------------------------------
// Depo arayüzü — arayüzün gördüğü tek yüz
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// KİLİTLİ DEFTER — Adım 1 (kullanıcı kararı 06.10.2026)
//
// Defter cihazda yalnızca şifreli kapta durur. Rastgele bir ANA ANAHTAR iki
// sarmalda saklanır: paroladan ve 24 kelimelik kurtarma ifadesinden türetilen
// anahtarlarla. Ana anahtar ve düz metin yalnızca Worker belleğinde. Kurallar
// `CLAUDE.md` §6; plan `claude/13-kilitli-defter-plan.md`.
// ---------------------------------------------------------------------------

/** Kurtarma ifadesinin kelime sayısı (BIP-39 İngilizce, 256 bit + 8 bit sağlama). */
export const KURTARMA_KELIME_SAYISI = 24;

/** Otomatik kilit seçenekleri, dakika (kullanıcı kararı D2). "Hiç" yok. */
export const KILIT_SURELERI_DK = [5, 15, 30, 60] as const;
export type KilitSuresiDk = (typeof KILIT_SURELERI_DK)[number];
export const VARSAYILAN_KILIT_SURESI_DK: KilitSuresiDk = 15;

/** Bu uzunluğun altındaki parolada UYARI (engel değil — kullanıcı kararı D1). */
export const PAROLA_UYARI_UZUNLUGU = 12;

/**
 * Şifreli kabın atomik yazım yolu. `tasi`: yeni dosya yazılıp yerine
 * taşınır (`move()`). `iki-yuva`: tarayıcıda `move()` yok, A/B yuvalarından
 * eskisi ezilir. Hangisinin kullanıldığı ölçülemediyse `null`.
 */
export type KapYazimYolu = "tasi" | "iki-yuva";

/** Kayıt defterindeki bir profilin dışarıdan görülebilen TEK bilgileri (CLAUDE.md §8). */
export interface ProfilOzeti {
  readonly id: string;
  readonly etiket: string;
  readonly olusturma: IsoTimestamp;
}

export interface KilitDurumu {
  /**
   * Açık profil; kilitliyken tek profil varsa o, birden çoksa en eski. Hiç
   * profil yoksa `null` (kurulum gerekli).
   */
  readonly profil: ProfilOzeti | null;
  /** ÇOKLU PROFİL (Adım 2): bu cihazdaki bütün profiller, oluşturma sırasıyla. */
  readonly profiller: readonly ProfilOzeti[];
  /** Ana anahtar bu Worker'da açık mı. */
  readonly acik: boolean;
  /** Taşınmayı bekleyen ŞİFRESİZ eski defter var mı (göç). */
  readonly sifresizDefterVar: boolean;
  readonly yazimYolu: KapYazimYolu | null;
  /**
   * Şifreli kaba son yazım başarısız olduysa sebebi (ör. depolama doldu).
   * Değişiklik bellekte duruyor ama cihaza YAZILMADI — sessiz kalamaz.
   */
  readonly sonYazimHatasi: string | null;
  /**
   * Açılışta son yazımın yarım kaldığı görüldü ve BİR ÖNCEKİ hâl açıldıysa
   * `"onceki"` — son değişikliklerden bir kısmı kayıp olabilir; söylenir.
   */
  readonly acilisKaynagi: "onceki" | null;
}

export interface KilitKurulumu {
  /** Kullanıcıya BİR KEZ gösterilecek kurtarma ifadesi. */
  readonly kelimeler: readonly string[];
  /** Şifresiz eski defter taşındıysa taşınan hareket sayısı; yeni defterde null. */
  readonly tasinanHareket: number | null;
}

/** Sır: parola YA DA kurtarma ifadesi. */
export type KilitSirri = { readonly parola: string } | { readonly kelimeler: readonly string[] };

/**
 * Kilidin RPC yüzü. Kilitliyken defterin her çağrısı `KILITLI`, kurulum
 * gerekiyorsa `KURULUM_GEREKLI` ile düşer; `open()` açıldıktan sonra çağrılır.
 */
export interface KilitPort {
  durum(): Promise<KilitDurumu>;
  /**
   * YENİ profil + ana anahtar + iki sarmal; açık oturum o olur. Şifresiz eski
   * defter yalnızca İLK profile (kayıt boşken) doğrulayarak taşınır.
   * `etiket` verilmezse "Defterim".
   */
  kur(parola: string, etiket?: string): Promise<KilitKurulumu>;
  /** `profilId` verilmezse tek profil; birden çok profil varken zorunlu. */
  ac(parola: string, profilId?: string): Promise<void>;
  /** Parolası unutulan defter: ifadeyle açılır, yeni parola AYNI anda yazılır. */
  acIfade(kelimeler: readonly string[], yeniParola: string, profilId?: string): Promise<void>;
  /**
   * BULUTTAN GETİR (kullanıcı kararı 19.10.2026): önce kasa kurmadan, buluttaki
   * `.pfai` v2 yedeğinden YENİ profil kurar. Profil yedeğin kendi kilit
   * kayıtlarıyla (sarmallar) kurulur: aynı parola ve aynı 24 kelime geçerli;
   * yeni parola/ifade yok. İçerik `open()`da yüklenir; açık oturum o olur.
   * `etiket` verilmezse yedeğin şifreli içindeki kasa adı; o da yoksa
   * VALIDATION (`kasa:ad-yok`). Aynı kasa bu cihazda zaten bir profilse
   * VALIDATION (`kasa:ayni`) — hiçbir şey yazılmaz. Eski (v1) dosya reddedilir.
   */
  yedektenKur?(metin: string, sir: KilitSirri, etiket?: string): Promise<{ readonly etiket: string }>;
  /** Açık profilin etiketi (yalnızca kayıt satırı değişir). */
  etiketDegistir(etiket: string): Promise<void>;
  /**
   * AÇIK profili bu cihazdan SİLER: kap, kuşaklar ve kayıt satırı birlikte
   * (CLAUDE.md §8). Buluttaki yedeklere dokunmaz. Geri alınamaz; arayüz açık
   * onay alır, ardından sayfayı yeniden yükler.
   */
  profilSil(): Promise<void>;
  /**
   * Açık profilin otomatik yedek kaydı (JSON metni) — profilin ŞİFRELİ
   * kabında, dışa aktarılmaz ve eşitlenmez. Yoksa `null`.
   */
  kasaOku(): Promise<string | null>;
  /** `null` = sil. */
  kasaYaz(metin: string | null): Promise<void>;
  /** Açıkken: parola sarmalı yeniden yazılır, kap yeniden şifrelenmez. */
  parolaDegistir(yeniParola: string): Promise<void>;
  /**
   * Açıkken: verilen parola AÇIK profilin parolası mı (parola sarmalı
   * çözülerek; bilerek yavaş). Parola hiçbir yerde saklanmadığı için "Parafon
   * sunucusu" hedefindeki uzunluk şartı ancak böyle sınanabilir (17.10.2026).
   */
  parolaDogrula?(parola: string): Promise<boolean>;
  /** Açıkken: yeni ifade, eski ifade sarmalı geçersiz olur. */
  ifadeYenile(): Promise<{ readonly kelimeler: readonly string[] }>;
  /** Bekleyen kap yazımını beklemeden yapar (kilitlemeden / sekme gizlenmeden önce). */
  yaz(): Promise<void>;
  /**
   * `DEFTER_KAYIP` sonrası çıkış yolu: açık oturumun anahtarıyla BOŞ bir kap
   * yazılır (kullanıcının açık onayıyla); ardından buluttan geri yüklenir.
   * Kap okunabiliyorsa reddedilir — var olan defterin üstüne yazmaz.
   */
  bosDefterleBasla(): Promise<void>;
  /**
   * KISA SÜRELİ OTURUM (kullanıcı kararı 12.10.2026): açık defterin anahtarı,
   * tarayıcının DIŞARI OKUNAMAYAN bir oturum anahtarıyla sarılıp bu cihazda
   * `sureDk` dakika saklanır; bu süre içinde sayfa yeniden yüklenirse defter
   * parola sorulmadan açılır. Her çağrı süreyi şu andan itibaren yeniler
   * (etkinlikle kayan süre). Kilitliyken bir şey yapmaz.
   */
  oturumuSurdur?(sureDk: number): Promise<void>;
  /** Saklanan oturumu siler ("Kilitle", otomatik kilit, profil değişimi). */
  oturumuUnut?(): Promise<void>;
}

/**
 * GRAFİK SERİSİ ÖNBELLEĞİ — profilin ŞİFRELİ kabında (cihaz v8), yedeğe
 * girmez. Hesaplanmış günler sayfa yeniden yüklense de kalır. Defter
 * değişince veritabanı tetikleyicileri hepsini, bar/kur/boşluk değişince o
 * günden sonrasını siler; künye ve şirket olayı değişimini eşitleme `sil` ile
 * bildirir. Motor sürümü değişen satır okunmaz.
 */
export interface SeriOnbellegiPort {
  oku(reportCurrency: CurrencyCode, from: IsoDate, to: IsoDate): Promise<readonly ValuationSeriesPoint[]>;
  yaz(reportCurrency: CurrencyCode, points: readonly ValuationSeriesPoint[]): Promise<void>;
  /** O gün ve sonrası; "all" = hepsi. */
  sil(from: IsoDate | "all"): Promise<void>;
}

/**
 * Bulut yedeğinin şifreli hâli — şifreleme Worker'da, ana anahtarla (`.pfai`
 * v2). Ana sayfa düz defteri ve anahtarı GÖRMEZ.
 */
export interface SifreliYedekPort {
  /** Açık defterin `.pfai` v2 metni. */
  olustur(): Promise<string>;
  /**
   * Buluttaki dosyadan (v1: yalnız parola; v2: parola ya da ifade) geri yükler.
   * Aynı defterin yedeğiyse BİRLEŞTİRİR. v2 başka bir deftere aitse ve bu
   * defter doluysa `BASKA_DEFTER` — hiçbir şey değişmez.
   */
  geriYukle(
    metin: string,
    sir: KilitSirri,
  ): Promise<{ readonly sonuc: ImportResult; readonly anahtarBenimsendi: boolean }>;
  /**
   * Bulut dosya adlarının öneki: `<defter>-<cihaz>`. Defter parçası defterin
   * ana anahtarından türer (aynı defterin bütün cihazlarında aynı, defteri
   * ele vermez); cihaz parçası profilden (iki cihazın dosyası birbirini
   * ezmez). Defter değişince (D5/D6) defter parçası da değişir.
   */
  dosyaOneki(): Promise<string>;
  /** Yalnızca defter parçası — öbür cihazların dosyalarını süzmek için. */
  defterParcasi(): Promise<string>;
  /**
   * EŞİTLEME: aynı defterin başka bir cihazdaki v2 yedeğini açık defterle
   * BİRLEŞTİRİR — sır gerekmez, dosya açık defterin anahtarıyla çözülür.
   * Çözülmezse (başka defter) `BASKA_DEFTER`, hiçbir şey değişmez.
   */
  esitle(metin: string): Promise<{ readonly sonuc: ImportResult }>;
  /** Dosya açık defterin anahtarıyla çözülüyor mu (bu deftere mi ait). Defteri değiştirmez. */
  aitMi(metin: string): Promise<boolean>;
  /**
   * Eşitleme turu bitti: "son eşitleme" izi bugünkü sayaca ilerler. Bundan
   * sonra bu cihazda yapılan değişiklikler bir sonraki turda çakışma
   * adayıdır. İz defterin şifreli kabında, dışa aktarılmaz.
   */
  esitlemeIziniIlerlet(): Promise<void>;
  /**
   * D6 · DEFTERİ DEĞİŞTİR: v2 yedekteki defter bu cihazdaki defterin YERİNE
   * geçer; cihaz o defterin ana anahtarını ve sarmallarını benimser. Eski
   * defter ve onun anahtarıyla yazılmış artıklar (önceki kap, kuşaklar)
   * ancak yeni kap yazılıp geri okunduktan sonra silinir; ondan önceki her
   * hatada eski defter olduğu gibi kalır. Yalnızca v2. Başarıdan sonra
   * arayüz sayfayı yeniden yükler.
   */
  defteriDegistir(metin: string, sir: KilitSirri): Promise<{ readonly sonuc: ImportResult }>;
}

export interface LocalRepository {
  /** Şema göçlerini uygular ve veritabanını kullanılabilir hâle getirir. */
  open(): Promise<RepositoryInfo>;
  close(): Promise<void>;

  accounts: {
    /** `devreDisiDahil` verilmezse devre dışı hesaplar listede yok (25.10.2026). */
    list(includeDeleted?: boolean, devreDisiDahil?: boolean): Promise<readonly Account[]>;
    get(id: string): Promise<Account | null>;
    create(input: NewAccount): Promise<Account>;
    update(id: string, patch: AccountPatch): Promise<Account>;
    /**
     * Tombstone: satır silinmez, `deleted` işaretlenir. Bağlı hareketler
     * `hareketler`e göre silinir (verilmezse SİLİNİR) ya da taşınır; hepsi
     * tek işlemde.
     */
    remove(id: string, hareketler?: HesapHareketSecimi): Promise<void>;
    /** Silinmiş/olmayan hesaplara bağlı canlı hareketler, hesap başına. */
    oksuzHareketler?(): Promise<readonly OksuzHareketGrubu[]>;
    /** Bir öksüz grubu siler ya da taşır; etkilenen hareket sayısı. */
    oksuzHareketleriCoz?(accountId: string, secim: HesapHareketSecimi): Promise<number>;
  };

  transactions: {
    query(filter?: TransactionFilter): Promise<TransactionPage>;
    get(id: string): Promise<Transaction | null>;
    create(input: NewTransaction): Promise<Transaction>;
    /** Tek transaction'da toplu ekleme — CSV içe aktarma bunu kullanır. */
    createMany(inputs: readonly NewTransaction[]): Promise<readonly Transaction[]>;
    update(id: string, patch: Partial<NewTransaction>): Promise<Transaction>;
    remove(id: string): Promise<void>;
  };

  manualValuations: {
    list(label?: string): Promise<readonly ManualValuation[]>;
    upsert(input: NewManualValuation): Promise<ManualValuation>;
    remove(id: string): Promise<void>;
  };

  manualPrices: {
    /** Belirli tarihteki fiyat; yoksa o tarihten ÖNCEKİ en yakın kayıt. */
    latestAsOf(instrumentRef: string, date: IsoDate): Promise<ManualPrice | null>;
    upsert(price: NewManualPrice): Promise<ManualPrice>;
    listFor(instrumentRef: string): Promise<readonly ManualPrice[]>;
    /** Tombstone — (instrumentRef, date) doğal anahtarıyla. */
    remove(instrumentRef: string, date: IsoDate): Promise<void>;
  };

  /** Aşama 1'in tek türetilmiş görünümü — HAM, maliyetsiz, çevrimsiz. */
  positions(asOf?: IsoDate): Promise<readonly RawPosition[]>;

  /**
   * AŞAMA 3'ÜN türetilmiş görünümü: FIFO maliyeti + son fiyat kuralıyla
   * değerleme + döviz çevrimi, her sayı kendi dayanağını taşıyarak.
   *
   * `positions()` KALDIRILMADI ve kaldırılmayacak: o, fiyat önbelleğine hiç
   * dokunmadan "elimde ne var" sorusunu yanıtlıyor ve fiyat verisi hiç
   * yokken de çalışıyor. Bu ikisi farklı iki soru.
   */
  valuation(options?: ValuationOptions): Promise<PortfolioReport>;

  /** Günlük seri — her gün için `valuation()`ın toplamları (grafik). */
  valuationSeries(options: ValuationSeriesOptions): Promise<ValuationSeries>;

  /**
   * DÖNEMSEL ANALİZ: seçilen araçların kayan periyot getirisi ve isteğe bağlı
   * sepet. Yalnızca fiyat önbelleğini okur; defter girmez. Fiyatları önce
   * çağıran eşitler. Sahte depo vermeyebilir (ikinci motor yazılmaz).
   */
  donemselAnaliz?(istek: DonemselAnalizIstegi): Promise<DonemselAnalizSonucu>;

  /**
   * SEPETİ HESAPLARDAN DOLDUR (kullanıcı, 15.10.2026): seçilen gerçek ve
   * sanal hesaplardaki varlıkların bugünkü değerinden oranlar. Para hesabı
   * motorda; arayüz yalnızca sonucu sepete yazar.
   */
  sepetDagilimi?(istek: SepetDagilimiIstegi): Promise<SepetDagilimi>;

  /**
   * Ana sayfa getiri özeti: `noktalar` = bugün dahil son 366 günün serisi
   * (sırasız olabilir). Sahte depo vermeyebilir.
   */
  getiriOzeti?(noktalar: readonly ValuationSeriesPoint[]): Promise<GetiriOzeti>;

  /** Risk ve dönem ölçüleri (seri noktalarından). Sahte depo vermeyebilir. */
  olculer?(istek: OlculerIstegi): Promise<Olculer>;

  /** Sanal hesaplar (back-test). Sahte depo vermeyebilir. */
  readonly sanalHesaplar?: SanalHesapPort;

  /**
   * TEK varlığın parti defteri — denetim için. Rapor bunu pozisyon başına
   * zaten taşıyor; buradaki yol "şu araçta ne oldu" sorusunu tüm portföyü
   * değerlemeden yanıtlamak için.
   */
  fifo(asset: AssetBinding): Promise<FifoResult>;

  settings: {
    get(key: string): Promise<string | null>;
    set(key: string, value: string): Promise<void>;
    all(): Promise<Readonly<Record<string, string>>>;
  };

  /**
   * ŞİFRESİZ tam yedek — dosyayı açan herkes bütün portföyü okur. Bir
   * geliştirici aracı ve acil çıkış kapısıdır.
   *
   * ÖNERİLEN YOL ARTIK `.pfai`: şifreli biçim (`packages/local/pfai.ts`)
   * kullanıcı kararıyla Aşama 4a'dan Aşama 3'ün BAŞINA alındı. Gerekçe: FIFO
   * ve vergi hesabının GERÇEK hareketlerle doğrulanması, uydurma test
   * verisiyle doğrulanmasından kıyaslanamaz ölçüde değerli — ve gerçek
   * portföyün girilebilmesi şifreli yedeğin var olmasına bağlıydı.
   *
   * Bu iki fonksiyon `.pfai`in de İÇ KATMANI: şifreli biçim, bu düz JSON'un
   * AES-GCM zarfından başka bir şey değil. Yani seri hâle getirme mantığı tek
   * yerde duruyor ve şifreleme onun üstüne biniyor.
   */
  exportPlainJson(): Promise<string>;
  importPlainJson(json: string): Promise<ImportResult>;

  /**
   * DIŞARIDAN İÇE AKTARMA (CSV ekranı, 09.10.2026). Kimliği verilmiş hesap ve
   * hareketler TEK işlemde: yoksa eklenir, aynıysa dokunulmaz, farklıysa
   * güncellenir. Parafon'da SİLİNMİŞ bir kayıt yeniden diriltilmez (kullanıcının
   * silmesi kazanır). Dosyada olmayan kayıt silinmez. Bir satır düşerse hiçbiri
   * yazılmaz. `deneme: true` hiçbir şey yazmadan aynı sınıflandırmayı verir
   * (önizleme).
   */
  iceAktar(paket: IceAktarmaPaketi, secenek?: { readonly deneme?: boolean }): Promise<IceAktarmaSonucu>;

  /** Kilitli defter (gerçek cihaz deposu). Sahte depo ve testler vermeyebilir. */
  readonly kilit?: KilitPort;
  readonly sifreliYedek?: SifreliYedekPort;
  /** Grafik serisi önbelleği (gerçek cihaz deposu). Sahte depo vermeyebilir. */
  readonly seriOnbellegi?: SeriOnbellegiPort;
}

export interface RepositoryInfo {
  /** Hangi VFS kullanıldı: Aşama 0'da opfs-sahpool + Worker doğrulandı. */
  vfs: string;
  schemaVersion: number;
  /** `navigator.storage.persist()` sonucu — Aşama 0'da false ölçüldü. */
  persistent: boolean;
  estimatedQuotaBytes: number | null;
  deviceId: string;
}

/**
 * Bir tablonun içe aktarma sonucu — DÖRT SAYI, DÖRT AYRI SORU.
 *
 * NİÇİN TEK SAYI YETMİYOR: canlı kullanımda ilk şifreli yedek geri
 * yüklendiğinde arayüz "Hesap: 0 / Hareket: 0" yazdı ve kullanıcı haklı
 * olarak verisini kaybettiğini düşündü. Oysa hiçbir şey kaybolmamıştı: dosya
 * AYNI cihazdan alınmıştı, kayıtların lamport'u ve cihaz kimliği birebir
 * aynıydı, dolayısıyla yazmaya GEREK yoktu. "Yazılan 0" ile "dosya boş" ve
 * "yerel kayıt daha yeni" birbirinden ayırt edilemediği için tek sayı
 * kullanıcıyı yanılttı.
 *
 * `found === written + unchanged + skipped` her zaman doğrudur; testler bunu
 * doğruluyor. Bu değişmez sayesinde arayüz hiçbir kaydı sessizce düşüremez.
 */
export interface ImportKindReport {
  /** Dosyada bu türden kaç kayıt vardı. */
  readonly found: number;
  /** Kaçı deftere yazıldı (yeni kayıt ya da üzerine yazma). */
  readonly written: number;
  /** Kaçı zaten AYNI sürümdü (lamport + cihaz kimliği birebir) — yazılmadı. */
  readonly unchanged: number;
  /** Kaçı atlandı: yerel sürüm daha yeni ya da kayıt bozuk. */
  readonly skipped: number;
}

export interface IceAktarmaPaketi {
  readonly accounts: readonly NewAccount[];
  readonly transactions: readonly NewTransaction[];
}

/** Kimlik başına ne olacağı / ne oldu. */
export type IceAktarmaDurumu = "yeni" | "guncellenecek" | "ayni" | "silinmis";

export interface IceAktarmaSonucu {
  readonly accounts: Readonly<Record<string, IceAktarmaDurumu>>;
  readonly transactions: Readonly<Record<string, IceAktarmaDurumu>>;
  /** `deneme` değilse yazıldı. */
  readonly yazildi: boolean;
}

export interface ImportResult {
  readonly accounts: ImportKindReport;
  readonly transactions: ImportKindReport;
  readonly manualValuations: ImportKindReport;
  readonly manualPrices: ImportKindReport;
  readonly settings: ImportKindReport;
  /**
   * Atlanan kayıtlar ve nedenleri — sessiz veri kaybı olmaz.
   *
   * `unchanged` BURAYA GİRMEZ: "zaten aynı" bir sorun değil, atlanan kayıt
   * listesine karışması kullanıcıya olmayan bir problem gösterirdi.
   */
  readonly skipped: readonly { kind: string; id: string; reason: string }[];
  /**
   * EŞİTLEME ÇAKIŞMALARI (yalnızca `SifreliYedekPort.esitle`): son eşitlemeden
   * sonra AYNI kaydı hem bu cihaz hem öbür cihaz değiştirmiş. Kayıt başına
   * sonraki değişiklik kazanır; kaybeden SESSİZCE gitmesin diye burada
   * söylenir. `etiket` kullanıcının tanıyacağı kısa ad (hesap adı, "tarih tür").
   */
  readonly cakismalar?: readonly EsitlemeCakismasi[];
}

export interface EsitlemeCakismasi {
  readonly kind: "account" | "transaction" | "manualValuation" | "manualPrice" | "setting";
  readonly id: string;
  readonly etiket: string;
  /** Hangi sürüm kaldı: bu cihazınki mi, öbür cihazınki mi. */
  readonly kazanan: "bu-cihaz" | "obur-cihaz";
}

// ---------------------------------------------------------------------------
// Worker RPC protokolü
// ---------------------------------------------------------------------------

/**
 * Ana thread ile veritabanı Worker'ı arasındaki mesaj biçimi. Kütüphanenin
 * `sqlite3Worker1Promiser`'ı 3.53'te deprecated olduğu için kendi ince
 * katmanımızı kullanıyoruz (Aşama 0 bulgusu).
 */
export interface RpcRequest {
  id: number;
  /** "accounts.create", "transactions.query", "positions" gibi. */
  method: string;
  params?: unknown;
}

export type RpcResponse =
  | { id: number; ok: true; result: unknown }
  | { id: number; ok: false; error: RpcError };

export interface RpcError {
  message: string;
  /** Arayüzün ayırt etmesi gereken durumlar. */
  code:
    | "LOCKED_BY_OTHER_TAB" // SAHPool tek sekmeye kilitler (Aşama 0'da ölçüldü)
    | "OPFS_UNAVAILABLE"
    | "MIGRATION_FAILED"
    | "NOT_FOUND"
    | "VALIDATION"
    | "TIMEOUT" // istek zaman aşımına uğradı — C şeridi UNKNOWN'a düşürüyordu
    // Uygulama dosyası (Worker betiği, cihaz modülü) sunucudan ALINAMADI: ağ
    // hatası, HTTP hatası ya da betik yerine HTML. Veritabanına HİÇ
    // dokunulmadı; sunucu dönünce yeniden denemek yeter. 27.09 olayında bu
    // durum "Worker çöktü" diye görünmüş ve site verisi temizletmişti.
    | "SERVER_UNAVAILABLE"
    // KİLİTLİ DEFTER (06.10.2026, `claude/13-kilitli-defter-plan.md`):
    // defter şifreli kapta, açmak için parola ya da kurtarma ifadesi gerekir.
    | "KILITLI"
    // Bu cihazda henüz defter kurulmamış (ya da şifresiz eski defter
    // taşınmayı bekliyor): parola + ifade kurulumu gerekir.
    | "KURULUM_GEREKLI"
    // Kayıt defteri var ama şifreli kap bulunamadı/okunamadı. Boş bir defter
    // UYDURULMAZ; kullanıcıya söylenir ve buluttan geri yükleme önerilir.
    | "DEFTER_KAYIP"
    // Geri yüklenmek istenen v2 yedek BAŞKA bir deftere ait (ana anahtarı
    // farklı) ve bu cihazdaki defter dolu. İki defter BİRLEŞTİRİLMEZ; hiçbir
    // şey değişmeden döner. Çıkış yolu `SifreliYedekPort.defteriDegistir`.
    | "BASKA_DEFTER"
    | "UNKNOWN";
  detail?: string;
}

/**
 * UYGULAMA DOSYASI YOKLAMASI — bir Worker ya da modül yüklenemediğinde
 * sebebini ölçer. Tarayıcı Worker yükleme hatasında sebep SÖYLEMEZ (çıplak
 * `error` olayı); "çöktü" demek tahmin olurdu. Yoklama aynı adrese tek bir
 * `HEAD` atar (servis işçisi `GET` dışındakilere dokunmaz, yani cevap
 * gerçekten sunucudan gelir):
 *
 *  - istek hiç cevaplanmadı ya da süresinde cevaplanmadı → `SERVER_UNAVAILABLE`
 *  - HTTP hatası → `SERVER_UNAVAILABLE` (sunucu cevap verdi ama dosyayı vermedi)
 *  - HTML döndü → `SERVER_UNAVAILABLE` (SPA geri dönüşü eksik dosyayı kabukla
 *    dolduruyor — 05 §"SPA geri dönüşü")
 *  - dosya yerinde ve betik → `null`: sunucu suçsuz, hata gerçekten
 *    çalışma anında; çağıran kendi sebebini söyler.
 *
 * `fetchFn` ve `online` dışarıdan verilir: tek kopya hem cihaz istemcisinde
 * hem arayüzde koşar, testte sahte ağla sınanır.
 */
export interface AppFileProbeDeps {
  readonly fetchFn: (
    url: string,
    init: { method: "HEAD"; cache: "no-store"; signal: AbortSignal },
  ) => Promise<{ readonly ok: boolean; readonly status: number; readonly headers: { get(name: string): string | null } }>;
  /** `navigator.onLine`; bilinmiyorsa `undefined`. Yalnızca `false` bir şey söyler. */
  readonly online?: boolean | undefined;
  readonly timeoutMs?: number;
}

export async function probeAppFile(url: string, deps: AppFileProbeDeps): Promise<RpcError | null> {
  const timeoutMs = deps.timeoutMs ?? 5_000;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let response: Awaited<ReturnType<AppFileProbeDeps["fetchFn"]>>;
  try {
    response = await deps.fetchFn(url, { method: "HEAD", cache: "no-store", signal: controller.signal });
  } catch (err) {
    const timedOut = controller.signal.aborted;
    const offline = deps.online === false;
    return {
      code: "SERVER_UNAVAILABLE",
      message: offline
        ? "Bu cihaz çevrimdışı; uygulama dosyası sunucudan alınamadı."
        : timedOut
          ? `Sunucu ${Math.round(timeoutMs / 1000)} sn içinde yanıt vermedi.`
          : "Sunucuya ulaşılamıyor.",
      detail: `${url} · ${timedOut ? "zaman aşımı" : err instanceof Error ? `${err.name}: ${err.message}` : String(err)}`,
    };
  } finally {
    clearTimeout(timer);
  }
  if (!response.ok) {
    return {
      code: "SERVER_UNAVAILABLE",
      message: `Sunucu uygulama dosyasını vermedi (HTTP ${response.status}).`,
      detail: url,
    };
  }
  const type = (response.headers.get("content-type") ?? "").toLowerCase();
  if (type.includes("text/html")) {
    return {
      code: "SERVER_UNAVAILABLE",
      message: "Sunucu uygulama dosyası yerine bir sayfa döndürdü (eksik ya da yarım yayın).",
      detail: `${url} · ${type}`,
    };
  }
  return null;
}

/** Ondalık doğrulaması: string, isteğe bağlı eksi, en fazla 12 ondalık hane. */
export const DECIMAL_PATTERN = /^-?\d{1,18}(\.\d{1,12})?$/;

export function isValidDecimal(value: string): boolean {
  return DECIMAL_PATTERN.test(value);
}

// ---------------------------------------------------------------------------
// AŞAMA 1 BÜTÜNLEŞTİRME DÜZELTMELERİ
//
// İki şerit (C: yerel çekirdek, E: arayüz) paralel çalışırken AYNI eksikleri
// bağımsız olarak buldu ve her biri kendi çözümünü uydurdu. Uydurulan iki
// çözüm bu kez uyumlu çıktı, ama olmayabilirdi — risk tablosundaki "sessiz
// hesap hatası" tam böyle doğar. Bu yüzden kurallar artık burada YAZILI ve
// çalışma zamanı değerleri tek yerden yayınlanıyor.
// ---------------------------------------------------------------------------

/**
 * NAKİT HAREKETLERİNİN KODLANMASI — bağlayıcı kural.
 *
 * `Transaction` ayrı bir `amount` alanı taşımaz. Nakit türlerinde
 * (DEPOSIT, WITHDRAWAL, FEE, INTEREST, DIVIDEND) kural şudur:
 *
 *     quantity = "1"   ve   price = tutar
 *
 * Böylece "brüt = quantity × price" bağıntısı HER türde geçerli kalır ve
 * hesap motoru tek bir formülle çalışır. Şema değişikliği gerekmez.
 * Arayüz kullanıcıya tek bir "tutar" alanı gösterir ve bu kodlamayı
 * kendi içinde yapar.
 */
export const CASH_TYPES: readonly TransactionType[] = [
  "DEPOSIT",
  "WITHDRAWAL",
  "FEE",
  "INTEREST",
  "DIVIDEND",
];

export function isCashType(type: TransactionType): boolean {
  return CASH_TYPES.includes(type);
}

/**
 * NAKİT HAREKETİNİN GETİRİDEKİ ROLÜ — H şeridinin tek doğruluk kaynağı.
 *
 * Üç rol var ve ayrımın tamamı şu cümlede: **sermaye hareketi getiri
 * değildir.** Hesaba 100.000 TL yatırmak portföyü 100.000 TL büyütür ama
 * kullanıcıyı 100.000 TL zengin ETMEZ. `DEPOSIT`/`WITHDRAWAL` getiri
 * hesabına girerse rapor, para yatırmayı kâr olarak gösterir — bu, motorun
 * üretebileceği en pahalı sessiz yalan olurdu.
 *
 * `null` dönen türler nakit türü değildir (miktar taşıyan alım-satım).
 */
export type CashFlowRole = "income" | "expense" | "capital";

export function cashFlowRole(type: TransactionType): CashFlowRole | null {
  switch (type) {
    case "DIVIDEND":
    case "INTEREST":
      return "income";
    case "FEE":
      return "expense";
    case "DEPOSIT":
    case "WITHDRAWAL":
    // Vergi ödemesi getirinin bir kalemi değil, nakdin hareketi: asıl getiriye
    // nakit (ya da dışarıdan giriş) üzerinden girer (19.10.2026).
    case "TAX_PAYMENT":
    // Döviz alış/satış hesabın İÇİNDE bir geçiştir: getiri değil, yatırma da
    // değil. Asıl getiri onu yatırılan saymaz, yalnızca iki kovayı oynatır
    // (22.10.2026).
    case "FX_CONVERSION":
      return "capital";
    default:
      return null;
  }
}

/**
 * POZİSYON İŞARET TABLOSU — `RawPosition.quantity` bununla toplanır.
 *
 * Önceki yorum "alım − satım + bedelsiz vb." diyordu; "vb." bir belirtim
 * değildir ve iki şerit farklı sayı üretebilirdi. Tablo artık kesin.
 *
 * DİKKAT — `SPLIT: 1` bir varsayım taşır: bölünme hareketinin `quantity`
 * alanı EKLENEN pay sayısıdır (yeni toplam değil). Aşama 3 şirket olaylarını
 * işlerken bu satır yeniden değerlendirilmeli.
 *
 * Nakit türleri 0'dır: nakit pozisyona girmez, çünkü pozisyon "elimde kaç
 * adet var" sorusunu yanıtlar. Nakit bakiyesi Aşama 3'ün işi.
 */
export const POSITION_SIGN: Readonly<Record<TransactionType, -1 | 0 | 1>> = {
  BUY: 1,
  SELL: -1,
  BONUS: 1,
  RIGHTS: 1,
  SPLIT: 1,
  TRANSFER_IN: 1,
  TRANSFER_OUT: -1,
  DIVIDEND: 0,
  INTEREST: 0,
  FEE: 0,
  TAX_PAYMENT: 0,
  FX_CONVERSION: 0,
  DEPOSIT: 0,
  WITHDRAWAL: 0,
};

/**
 * NAKİT İŞARET TABLOSU — net nakit hareketinin TEK doğruluk kaynağı.
 *
 * `POSITION_SIGN`in kardeşi ve aynı sebeple burada: o tablo "elimde kaç adet
 * var" sorusunu, bu tablo "bakiyeye ne oldu" sorusunu yanıtlıyor. İkisi ayrı
 * sorular ve bir türün birindeki işareti ötekini BELİRLEMİYOR — `TRANSFER_IN`
 * pozisyonu artırır ama nakde dokunmaz, `DIVIDEND` nakdi artırır ama pozisyona
 * dokunmaz.
 *
 * KURAL: `net = CASH_SIGN[type] × quantity × price − fee − tax`
 *
 * `fee` ve `tax` HER türde ÇIKARILIR ve bu bir kısaltma değil: ikisi de
 * hesaptan çıkan paradır, hangi hareketin yanında yazıldığından bağımsız.
 * Temettüde `tax` stopajdır (brütü alıp stopajı ayrıca çıkarmak, net eline
 * geçeni verir); satışta da öyle.
 *
 * ÜÇ SATIR AÇIKLAMA İSTİYOR:
 *
 *  - `TRANSFER_IN`/`TRANSFER_OUT` = 0. Bunlar MENKUL devridir, nakit devri
 *    değil. `price` alanları devralınan MALİYETİ taşır (bkz. "maliyetsiz
 *    devir"), ödenen parayı değil; işareti ±1 yapmak, hiç yapılmamış bir
 *    ödemeyi bakiyeye yazardı. Masrafı varsa o yine çıkıyor.
 *  - `RIGHTS` = −1. Rüçhan BEDELLİDİR: yeni pay bedeli ödenerek alınır.
 *    `BONUS` (bedelsiz) ile aynı kefeye konmamalı.
 *  - `BONUS`/`SPLIT` = 0. Karşılığında para ödenmez; ödenen bir masraf varsa
 *    yalnızca o çıkar.
 *
 * HESABIN PARA BİRİMİ SORULMUYOR: işaret hareketin KENDİ para biriminde
 * uygulanır ve kovalar (hesap × para birimi) ayrı tutulur. TRY hesabına
 * yazılmış bir USD hareketi TRY'ye çevrilmez — çevirmek "bozdurdunuz" demek
 * olurdu ve defterde bunu söyleyen bir satır yok.
 */
export const CASH_SIGN: Readonly<Record<TransactionType, -1 | 0 | 1>> = {
  BUY: -1,
  SELL: 1,
  RIGHTS: -1,
  BONUS: 0,
  SPLIT: 0,
  TRANSFER_IN: 0,
  TRANSFER_OUT: 0,
  DEPOSIT: 1,
  WITHDRAWAL: -1,
  DIVIDEND: 1,
  INTEREST: 1,
  FEE: -1,
  /** Vergi ödemesi hesaptan çıkan paradır (kaynağı "dışarıdan" ise motor aynı gün bir giriş sayar). */
  TAX_PAYMENT: -1,
  /**
   * Döviz alış/satış: bu işaret VERİLEN tarafındır (`currency`, `price`).
   * Alınan taraf (`alinanTutar`, `alinanParaBirimi`) kendi kovasına +1 girer;
   * motor onu ayrı bir bacak olarak uygular (22.10.2026).
   */
  FX_CONVERSION: -1,
};

/** Çalışma zamanı listeleri — iki şerit de bunları aynalamak zorunda kalmıştı. */
export const TRANSACTION_TYPES: readonly TransactionType[] = [
  "BUY",
  "SELL",
  "DIVIDEND",
  "INTEREST",
  "SPLIT",
  "BONUS",
  "RIGHTS",
  "DEPOSIT",
  "WITHDRAWAL",
  "FEE",
  "TRANSFER_IN",
  "TRANSFER_OUT",
  "TAX_PAYMENT",
  "FX_CONVERSION",
];

export const ACCOUNT_KINDS: readonly AccountKind[] = [
  "araci_kurum",
  "banka",
  "cuzdan",
  "borsa",
  "fiziki",
];

/**
 * SIRALAMA GARANTİSİ — `limit`/`offset` sayfalaması ancak kararlı bir sıra
 * varsa doğru çalışır; yoksa satır atlar veya tekrarlar.
 *
 * `transactions.query` şu sırayı GARANTİ EDER:
 *     tradeDate AZALAN, sonra id AZALAN
 *
 * `id` ULID olduğu için zaman sıralıdır ve eşitlik durumunda kararlı bir
 * ikincil anahtar sağlar.
 */
export const TRANSACTION_SORT = "tradeDate DESC, id DESC" as const;

/**
 * HATA YÜZEYİ — `LocalRepository` metotları başarısızlıkta bu şekle uyan bir
 * nesne FIRLATIR (reject eder). Arayüzün üç ayrı olasılığı yoklaması
 * gerekmesin diye tek biçim seçildi: fırlatılan değer bir `Error`dır ve
 * `code`/`detail` alanlarını doğrudan taşır.
 */
export interface RepositoryFailure extends Error {
  code: RpcError["code"];
  detail?: string;
}

export function isRepositoryFailure(value: unknown): value is RepositoryFailure {
  if (typeof value !== "object" || value === null) return false;
  const code = (value as { code?: unknown }).code;
  return typeof code === "string" && REPOSITORY_ERROR_CODES.includes(code as RpcError["code"]);
}

export const REPOSITORY_ERROR_CODES: readonly RpcError["code"][] = [
  "LOCKED_BY_OTHER_TAB",
  "OPFS_UNAVAILABLE",
  "MIGRATION_FAILED",
  "NOT_FOUND",
  "VALIDATION",
  "TIMEOUT",
  "SERVER_UNAVAILABLE",
  "KILITLI",
  "KURULUM_GEREKLI",
  "DEFTER_KAYIP",
  "BASKA_DEFTER",
  "UNKNOWN",
];

/**
 * SEANS DIŞI FİYAT AYARI (kullanıcı kararı 04.11.2026): kasanın şifreli
 * ayarlarında; "1" = bugünkü değer seans öncesi/sonrası fiyatla (varsa ve
 * normal seans fiyatından yeniyse), başka her değer ya da yok = normal seans
 * kapanışıyla. Geçmiş günler her zaman kapanışla. Ücretsiz kasada da açık.
 */
export const SEANS_DISI_AYARI = "seansDisiFiyat";
